import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError, apiJson, formatApiErrorForDisplay } from '@/lib/api';
import { track } from '@/lib/analytics';
import { AdminUnreadDot } from '@/components/admin/AdminUnreadDot';
import { OutreachReplyAttachmentPicker } from '@/components/admin/OutreachReplyAttachmentPicker';
import { sendOutreachReply } from '@/lib/outreachReplySend';
import { useCountyCourtOutreachUnreadReplies } from '@/hooks/useCountyCourtOutreachUnreadReplies';
import { COUNTY_COURT_OUTREACH_REPLIES_QUERY_KEY } from '@/lib/countyCourtOutreachRepliesStorage';
import { LEAVE_BEHIND_PDF, LEAVE_BEHIND_PDF_DOWNLOAD } from '@/lib/leave-behind';

export const Route = createFileRoute('/admin/county-court-outreach')({
  component: CountyCourtOutreachAdminPage,
});

const STATS_KEY = ['admin', 'county-court-outreach-stats'] as const;
const COUNTIES_KEY = ['admin', 'county-court-outreach-counties'] as const;
const DRAFTS_KEY = ['admin', 'county-court-outreach-drafts'] as const;
const CONTROL_KEY = ['admin', 'county-court-outreach-control'] as const;
const MACROS_KEY = ['admin', 'county-court-outreach-macros'] as const;
const PULL_RUNS_KEY = ['admin', 'county-court-outreach-pull-runs'] as const;
const RESEARCH_BOARD_COLLAPSED_KEY =
  'harbor.admin.county-court-outreach.research-board-collapsed';

function readResearchBoardCollapsed(): boolean {
  try {
    const stored = window.localStorage.getItem(RESEARCH_BOARD_COLLAPSED_KEY);
    if (stored === '1') return true;
    if (stored === '0') return false;
    return window.matchMedia('(max-width: 1023px)').matches;
  } catch {
    return false;
  }
}

function writeResearchBoardCollapsed(collapsed: boolean) {
  try {
    window.localStorage.setItem(
      RESEARCH_BOARD_COLLAPSED_KEY,
      collapsed ? '1' : '0',
    );
  } catch {
    // Ignore quota / private-mode failures; the in-session toggle still works.
  }
}

function parseDetail(err: unknown): string {
  return formatApiErrorForDisplay(err);
}

type ConfirmDialogState = {
  title: string;
  message: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
};

type StatsResponse = {
  seeded_counties: number;
  expected_counties: number;
  expected_california_counties: number;
  expected_by_state?: Record<string, number>;
  registered_states?: string[];
  pulled_count: number;
  have_list_count: number;
  harbor_listed_count: number;
  needs_email_count: number;
  needs_research_count: number;
  failed_pull_count: number;
  formal_list_count: number;
  contacts_with_email: number;
  approved_email_records: number;
  sent_email_records: number;
  draft_email_records: number;
};

type SuggestedContact = {
  name: string | null;
  title: string | null;
  email: string | null;
  phone: string | null;
  confidence: string;
};

type PullSuggestions = {
  emails: string[];
  phones: string[];
  contacts: SuggestedContact[];
  resource_urls: string[];
  page_urls: string[];
};

type PullRow = {
  id: number;
  source_url: string;
  status: string;
  listed_apps: string[];
  harbor_listed: boolean;
  excerpt: string | null;
  error: string | null;
  error_short?: string | null;
  suggestions?: PullSuggestions;
  created_at: string;
};

type CountyRow = {
  contact_id: number;
  county_name: string;
  county_slug: string;
  state?: string;
  state_name?: string | null;
  jurisdiction_kind?: string;
  jurisdiction_name?: string | null;
  timezone?: string | null;
  court_website: string | null;
  self_help_url: string | null;
  fcs_url: string | null;
  resource_list_url: string | null;
  resource_list_kind: string;
  listed_apps: string[];
  harbor_listed: boolean;
  last_pulled_at: string | null;
  last_pull_status: string | null;
  last_pull_source_url: string | null;
  last_pull_excerpt: string | null;
  last_pull_error?: string | null;
  last_pull_error_short?: string | null;
  suggestions?: PullSuggestions;
  office_type: string;
  office_type_label: string;
  contact_name: string | null;
  contact_title: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  notes: string | null;
  summary: string | null;
  tier: number | null;
  converted: boolean;
  recent_pulls: PullRow[];
};

type CountyListResponse = {
  rows: CountyRow[];
  total: number;
  office_type_labels: Record<string, string>;
  resource_list_kind_labels: Record<string, string>;
  registered_states?: string[];
  state?: string | null;
};

type PriorTouch = {
  email_id: number;
  email_key: string;
  status: string;
  subject: string | null;
  body: string | null;
  sent_at: string | null;
};

type DraftRow = {
  contact_id: number;
  county_name: string;
  state?: string | null;
  state_name?: string | null;
  jurisdiction_kind?: string | null;
  jurisdiction_name?: string | null;
  contact_name: string | null;
  contact_title: string | null;
  office_type: string;
  email: string | null;
  last_pull_source_url?: string | null;
  self_help_url?: string | null;
  fcs_url?: string | null;
  resource_list_url?: string | null;
  court_website?: string | null;
  resource_list_kind: string;
  listed_apps: string[];
  harbor_listed: boolean;
  notes: string | null;
  contact_status: string;
  tier: number | null;
  email_id: number | null;
  email_key: string;
  email_status: string | null;
  subject: string | null;
  body: string | null;
  prior_touches: PriorTouch[];
};

type TopSubject = {
  template: string;
  reply_count: number;
  sent_count: number;
  macro_id: number | null;
};

type DraftListResponse = {
  rows: DraftRow[];
  total: number;
  email_key: string;
  top_subject: TopSubject | null;
};

type SwitchTopSubjectResponse = {
  email_id: number;
  contact_id: number;
  email_key: string;
  status: string;
  subject: string;
  previous_subject: string | null;
  top_subject: TopSubject;
  already_using: boolean;
  message: string;
};

type ControlStatus = {
  enabled: boolean;
  enabled_source: string;
  paused: boolean;
  redis_configured: boolean;
  daily_limit: number;
  daily_limit_source: string;
  sent_today: number;
  approved_count: number;
  schedule_environment: string;
  schedule_description: string;
  next_run_at_utc: string | null;
};

const DAILY_LIMIT_MIN = 0;
const DAILY_LIMIT_MAX = 100;

function dailyLimitSourceLabel(source: string): string {
  if (source === 'redis') return 'admin override';
  if (source === 'env_var') return 'env var';
  return 'default';
}

function parseDailyLimitInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isInteger(value)) return null;
  if (value < DAILY_LIMIT_MIN || value > DAILY_LIMIT_MAX) return null;
  return value;
}

function canSaveDailyLimit(raw: string, current: number): boolean {
  const next = parseDailyLimitInput(raw);
  return next != null && next !== current;
}

type MacroRow = {
  id: number;
  name: string;
  description: string | null;
  email_key: string | null;
  subject: string;
  body: string;
  sent_count: number;
  reply_count: number;
  created_at: string;
  updated_at: string;
};

type MacroListResponse = {
  macros: MacroRow[];
};

type MacroSaveResponse = {
  macro: MacroRow;
  created: boolean;
  message: string;
};

type MacroDeleteResponse = {
  deleted_id: number;
  message: string;
};

type CountyCourtOutreachReplyContactRef = {
  id: number;
  county_name: string;
  contact_name: string | null;
  email: string | null;
  status: string;
};

type CountyCourtOutreachReplyRow = {
  id: string;
  from_email: string;
  to_emails: string[];
  subject: string;
  received_at: string | null;
  message_id: string | null;
  has_attachments: boolean;
  matched_contact: CountyCourtOutreachReplyContactRef | null;
  manual_reply_sent_at: string | null;
};

type CountyCourtOutreachRepliesListResponse = {
  replies: CountyCourtOutreachReplyRow[];
  has_more: boolean;
  fetched_at: string;
  error: string | null;
};

type CountyCourtOutreachReplyDetailResponse = {
  id: string;
  from_email: string;
  to_emails: string[];
  cc: string[];
  bcc: string[];
  reply_to: string[];
  subject: string;
  received_at: string | null;
  message_id: string | null;
  html: string | null;
  text: string | null;
  attachments: Array<{
    id?: string;
    filename?: string;
    content_type?: string;
    content_disposition?: string;
  }>;
  matched_contact: CountyCourtOutreachReplyContactRef | null;
  manual_reply_sent_at: string | null;
};

type CountyCourtOutreachReplySendRequest = {
  subject: string;
  body: string;
  include_quoted_original: boolean;
  files: File[];
};

type CountyCourtOutreachReplySendResponse = {
  message_id: string;
  from_email: string;
  to_email: string;
  subject: string;
  sent_at: string;
  contact_status_after: string | null;
  attachment_filenames?: string[];
  persisted_email_id: number | null;
};

type CountyCourtOutreachNeverEmailResponse = {
  contact_id: number;
  status: string;
  skipped_drafts: number;
  message: string;
};

type PreviewResponse = {
  contact_id: number;
  county_name: string;
  state?: string | null;
  jurisdiction_name?: string | null;
  to_email: string | null;
  from_email: string;
  reply_to: string | null;
  subject: string;
  html: string;
  text: string;
  headers: Record<string, string>;
  unsubscribe_url: string;
};

type PullRunRow = {
  id: number;
  job_id: string | null;
  status: string;
  trigger: string;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  state_count: number;
  states: string | null;
  batch_size: number;
  pulled_ok: number;
  pulled_error: number;
  skipped_no_url: number;
  emails_found: number;
  emails_autofilled: number;
  phones_found: number;
  phones_autofilled: number;
  names_autofilled: number;
  apps_found: number;
  harbor_listed: number;
  error: string | null;
  created_at: string;
};

type PullRunListResponse = {
  runs: PullRunRow[];
  latest: PullRunRow | null;
};

const TOUCH_LABELS: Record<string, string> = {
  touch_1: 'Touch 1',
  touch_2: 'Touch 2',
  touch_3: 'Touch 3',
};

type MacroCountyContext = Pick<
  CountyRow,
  | 'county_name'
  | 'contact_name'
  | 'contact_title'
  | 'office_type'
  | 'office_type_label'
  | 'state'
  | 'state_name'
  | 'jurisdiction_name'
>;

function jurisdictionLabel(row: {
  state?: string | null;
  county_name: string;
  jurisdiction_name?: string | null;
}): string {
  const name = row.jurisdiction_name || row.county_name;
  return row.state ? `${row.state} · ${name}` : name;
}

const RESEARCH_FILTERS = [
  { value: 'all', label: 'All counties' },
  { value: 'needs_research', label: 'Needs research' },
  { value: 'pull_failed', label: 'Pull failed' },
  { value: 'has_list_harbor_missing', label: 'Has list, Harbor missing' },
  { value: 'no_list', label: 'No list found' },
  { value: 'needs_email', label: 'Needs email' },
  { value: 'has_email', label: 'Has email' },
  { value: 'harbor_listed', label: 'Harbor listed' },
  { value: 'formal', label: 'Formal / RFP' },
];

const EMPTY_SUGGESTIONS: PullSuggestions = {
  emails: [],
  phones: [],
  contacts: [],
  resource_urls: [],
  page_urls: [],
};

const STATUS_FILTERS = [
  { value: 'needs_review', label: 'Needs review' },
  { value: 'due', label: 'Due for follow-up' },
  { value: 'approved', label: 'Approved' },
  { value: 'sent', label: 'Sent' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'all', label: 'All' },
];

function draftStatusBadge(status: string | null): {
  label: string;
  className: string;
} {
  switch (status) {
    case 'approved':
      return {
        label: 'Approved',
        className: 'bg-emerald-50 text-emerald-800 border-emerald-200',
      };
    case 'sent':
      return {
        label: 'Sent',
        className: 'bg-sky-50 text-sky-800 border-sky-200',
      };
    case 'skipped':
      return {
        label: 'Skipped',
        className: 'bg-gray-100 text-gray-600 border-gray-200',
      };
    case 'draft':
      return {
        label: 'Draft',
        className: 'bg-amber-50 text-amber-800 border-amber-200',
      };
    default:
      return {
        label: status || 'No draft',
        className: 'bg-gray-50 text-gray-600 border-gray-200',
      };
  }
}

function hScrollPillRowClassName() {
  return '-mx-1 overflow-x-auto overscroll-x-contain pb-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden';
}

function useIsLg() {
  const [isLg, setIsLg] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsLg(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isLg;
}

function firstName(contactName: string | null | undefined): string {
  const token = (contactName || '').trim().split(/\s+/)[0];
  return token || 'there';
}

function applyMacroPlaceholders(
  text: string,
  county: MacroCountyContext | null,
): { value: string; substitutions: string[] } {
  if (!county) return { value: text, substitutions: [] };
  const subs = new Set<string>();
  const value = text
    .replace(/\{\{\s*county_name\s*\}\}/g, () => {
      if (county.county_name) subs.add('county_name');
      return county.county_name || '';
    })
    .replace(/\{\{\s*contact_name\s*\}\}/g, () => {
      const name = county.contact_name || county.office_type_label;
      if (name) subs.add('contact_name');
      return name || '';
    })
    .replace(/\{\{\s*first_name\s*\}\}/g, () => {
      if (county.contact_name) subs.add('first_name');
      return firstName(county.contact_name);
    })
    .replace(/\{\{\s*office_type\s*\}\}/g, () => {
      const label = county.office_type_label || county.office_type;
      if (label) subs.add('office_type');
      return label || '';
    })
    .replace(/\{\{\s*contact_title\s*\}\}/g, () => {
      const title = county.contact_title || county.office_type_label;
      if (title) subs.add('contact_title');
      return title || '';
    })
    .replace(/\{\{\s*jurisdiction_name\s*\}\}/g, () => {
      const name = county.jurisdiction_name || county.county_name;
      if (name) subs.add('jurisdiction_name');
      return name || '';
    })
    .replace(/\{\{\s*office_label\s*\}\}/g, () => {
      const label = county.office_type_label || county.office_type;
      if (label) subs.add('office_label');
      return label || '';
    })
    .replace(/\{\{\s*state_name\s*\}\}/g, () => {
      const name = county.state_name || county.state || '';
      if (name) subs.add('state_name');
      return name;
    });
  return { value, substitutions: [...subs] };
}

function countyContextForMacros(
  selectedCounty: CountyRow | null,
  selectedDraft: DraftRow | null,
  officeLabels: Record<string, string>,
): MacroCountyContext | null {
  if (
    selectedCounty &&
    selectedDraft &&
    selectedCounty.contact_id === selectedDraft.contact_id
  ) {
    return selectedCounty;
  }
  if (selectedDraft) {
    return {
      county_name: selectedDraft.county_name,
      contact_name: selectedDraft.contact_name,
      contact_title: selectedDraft.contact_title,
      office_type: selectedDraft.office_type,
      office_type_label:
        officeLabels[selectedDraft.office_type] || selectedDraft.office_type,
      state: selectedDraft.state ?? undefined,
      state_name: selectedDraft.state_name ?? undefined,
      jurisdiction_name: selectedDraft.jurisdiction_name ?? undefined,
    };
  }
  return selectedCounty;
}

function kindBadge(kind: string, pullStatus?: string | null): string {
  if (pullStatus === 'error') {
    return 'bg-red-50 text-red-800 border-red-200';
  }
  if (kind === 'formal_vendor' || kind === 'rfp') {
    return 'bg-purple-50 text-purple-800 border-purple-200';
  }
  if (kind === 'informal') return 'bg-sky-50 text-sky-800 border-sky-200';
  if (kind === 'none') return 'bg-gray-100 text-gray-600 border-gray-200';
  return 'bg-amber-50 text-amber-800 border-amber-200';
}

function listKindLabel(
  row: Pick<CountyRow, 'resource_list_kind' | 'last_pull_status'>,
  kindLabels: Record<string, string>,
): string {
  if (row.last_pull_status === 'error') return 'Pull failed';
  return kindLabels[row.resource_list_kind] || row.resource_list_kind;
}

function listColumnSubtext(row: CountyRow): { text: string; className: string } {
  const suggestions = row.suggestions ?? EMPTY_SUGGESTIONS;
  if (row.last_pull_status === 'error') {
    return {
      text: row.last_pull_error_short || 'Could not fetch the page',
      className: 'text-red-600',
    };
  }
  if (row.harbor_listed) {
    return { text: 'Harbor listed', className: 'text-emerald-700' };
  }
  if (row.listed_apps.length) {
    return { text: row.listed_apps.join(', '), className: 'text-gray-500' };
  }
  if (suggestions.emails.length) {
    const count = suggestions.emails.length;
    return {
      text: `${count} email${count === 1 ? '' : 's'} found`,
      className: 'text-sky-700',
    };
  }
  if (row.last_pulled_at) {
    return { text: 'No apps found', className: 'text-gray-500' };
  }
  return { text: 'Not pulled', className: 'text-gray-400' };
}

function formatPulledAt(value: string | null): string {
  if (!value) return 'Never pulled';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function canSwitchDraftSubject(status: string | null): boolean {
  return status === 'draft' || status === 'approved';
}

function subjectsMatch(left: string | null | undefined, right: string): boolean {
  return (left || '').trim() === right.trim();
}

function topSubjectFromMacros(
  macros: MacroRow[] | undefined,
  emailKey: string,
): TopSubject | null {
  const eligible = (macros ?? []).filter(
    (macro) => macro.email_key == null || macro.email_key === emailKey,
  );
  if (!eligible.length) return null;
  const winner = [...eligible].sort((a, b) => {
    if (b.reply_count !== a.reply_count) return b.reply_count - a.reply_count;
    if (b.sent_count !== a.sent_count) return b.sent_count - a.sent_count;
    return a.name.localeCompare(b.name);
  })[0];
  return {
    template: winner.subject,
    reply_count: winner.reply_count,
    sent_count: winner.sent_count,
    macro_id: winner.id,
  };
}

function draftSourceUrl(row: DraftRow): string | null {
  return (
    row.last_pull_source_url ||
    row.self_help_url ||
    row.fcs_url ||
    row.resource_list_url ||
    row.court_website ||
    null
  );
}

const COUNTY_COURT_OUTREACH_FROM_DISPLAY =
  'Jose at Harbor <jose@hello.harborcoparent.com>';

function dedupeReplySubject(subject: string): string {
  const base = (subject ?? '').trim();
  if (!base) return 'Re:';
  if (/^(re|fwd|fw):/i.test(base)) return base;
  return `Re: ${base}`;
}

function formatTimestamp(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatRelative(value: string | null): string {
  if (!value) return '—';
  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return value;
  const diffMs = Date.now() - ts;
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(value).toLocaleDateString();
}

function matchedCountyLabel(
  contact: CountyCourtOutreachReplyContactRef | null,
): string {
  if (!contact) return '';
  return contact.contact_name
    ? `${contact.contact_name} · ${contact.county_name}`
    : contact.county_name;
}

function formatDurationMs(ms: number | null): string {
  if (ms == null) return 'n/a';
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
}

function pullRunStatusLabel(run: PullRunRow): string {
  if (run.status === 'queued') return 'Queued';
  if (run.status === 'running') return 'Running';
  if (run.status === 'error') return 'Failed';
  return formatDurationMs(run.duration_ms);
}

function isActivePullRun(status: string | undefined): boolean {
  return status === 'queued' || status === 'running';
}

function CountyCourtOutreachAdminPage() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const isLg = useIsLg();
  const [researchFilter, setResearchFilter] = useState('all');
  const [selectedState, setSelectedState] = useState('');
  const [rePullWithEmail, setRePullWithEmail] = useState(false);
  const [researchBoardCollapsed, setResearchBoardCollapsed] = useState(
    readResearchBoardCollapsed,
  );
  const [countyQuery, setCountyQuery] = useState('');
  const [selectedCountyId, setSelectedCountyId] = useState<number | null>(null);
  const [emailKey, setEmailKey] = useState('touch_1');
  const [draftStatus, setDraftStatus] = useState('needs_review');
  const [draftQuery, setDraftQuery] = useState('');
  const [selectedDraftId, setSelectedDraftId] = useState<number | null>(null);
  const [gymEditorOpen, setGymEditorOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);
  const [macroSaveName, setMacroSaveName] = useState('');
  const [macroSaveDescription, setMacroSaveDescription] = useState('');
  const [macroSaveScopeToTouch, setMacroSaveScopeToTouch] = useState(false);
  const [macroBanner, setMacroBanner] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(
    null,
  );
  const [previewResult, setPreviewResult] = useState<PreviewResponse | null>(
    null,
  );
  const [previewMode, setPreviewMode] = useState<'html' | 'text'>('html');
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [dailyLimitDraft, setDailyLimitDraft] = useState('');
  const seenRunningPullRunId = useRef<number | null>(null);
  const [edit, setEdit] = useState({
    contact_name: '',
    contact_title: '',
    email: '',
    phone: '',
    office_type: 'fcs_director',
    resource_list_url: '',
    self_help_url: '',
    fcs_url: '',
    court_website: '',
    notes: '',
  });

  useEffect(() => {
    track('County Court Outreach Screen');
  }, []);

  const statsQuery = useQuery({
    queryKey: STATS_KEY,
    queryFn: () =>
      apiJson<StatsResponse>(getToken, '/v1/admin/county-court-outreach/stats'),
  });

  const countiesQuery = useQuery({
    queryKey: [...COUNTIES_KEY, researchFilter, countyQuery, selectedState],
    queryFn: () => {
      const params = new URLSearchParams({
        research_filter: researchFilter,
        limit: '200',
      });
      if (countyQuery.trim()) params.set('q', countyQuery.trim());
      if (selectedState) params.set('state', selectedState);
      return apiJson<CountyListResponse>(
        getToken,
        `/v1/admin/county-court-outreach/counties?${params}`,
      );
    },
  });

  const draftsQuery = useQuery({
    queryKey: [...DRAFTS_KEY, emailKey, draftStatus, draftQuery, selectedState],
    queryFn: () => {
      const params = new URLSearchParams({
        email_key: emailKey,
        status: draftStatus,
        limit: '200',
      });
      if (draftQuery.trim()) params.set('q', draftQuery.trim());
      if (selectedState) params.set('state', selectedState);
      return apiJson<DraftListResponse>(
        getToken,
        `/v1/admin/county-court-outreach/drafts?${params}`,
      );
    },
  });

  const controlQuery = useQuery({
    queryKey: CONTROL_KEY,
    queryFn: () =>
      apiJson<ControlStatus>(getToken, '/v1/admin/county-court-outreach/control'),
  });

  const macrosQuery = useQuery({
    queryKey: MACROS_KEY,
    queryFn: () =>
      apiJson<MacroListResponse>(
        getToken,
        '/v1/admin/county-court-outreach/macros',
      ),
  });

  const pullRunsQuery = useQuery({
    queryKey: PULL_RUNS_KEY,
    queryFn: () =>
      apiJson<PullRunListResponse>(
        getToken,
        '/v1/admin/county-court-outreach/pull-runs?limit=8',
      ),
    refetchInterval: (query) =>
      isActivePullRun(query.state.data?.latest?.status) ? 4000 : false,
  });

  const counties = countiesQuery.data?.rows ?? [];
  const selectedCounty =
    counties.find((row) => row.contact_id === selectedCountyId) ?? counties[0] ?? null;
  const drafts = draftsQuery.data?.rows ?? [];
  const topSubject =
    draftsQuery.data?.top_subject ??
    topSubjectFromMacros(macrosQuery.data?.macros, emailKey);
  const selectedDraft =
    drafts.find((row) => row.contact_id === selectedDraftId) ?? drafts[0] ?? null;

  useEffect(() => {
    if (!selectedCounty) return;
    setEdit({
      contact_name: selectedCounty.contact_name ?? '',
      contact_title: selectedCounty.contact_title ?? '',
      email: selectedCounty.email ?? '',
      phone: selectedCounty.phone ?? '',
      office_type: selectedCounty.office_type,
      resource_list_url: selectedCounty.resource_list_url ?? '',
      self_help_url: selectedCounty.self_help_url ?? '',
      fcs_url: selectedCounty.fcs_url ?? '',
      court_website: selectedCounty.court_website ?? '',
      notes: selectedCounty.notes ?? '',
    });
  }, [selectedCounty?.contact_id]);

  useEffect(() => {
    if (!selectedDraft) {
      setSubject('');
      setBody('');
      return;
    }
    setSubject(selectedDraft.subject ?? '');
    setBody(selectedDraft.body ?? '');
  }, [selectedDraft?.contact_id, selectedDraft?.email_key, selectedDraft?.email_id]);

  useEffect(() => {
    setGymEditorOpen(false);
  }, [emailKey, draftStatus, draftQuery, selectedState]);

  useEffect(() => {
    if (draftsQuery.isSuccess && drafts.length === 0) {
      setGymEditorOpen(false);
    }
  }, [draftsQuery.isSuccess, drafts.length]);

  useEffect(() => {
    if (!gymEditorOpen) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [gymEditorOpen, selectedDraft?.contact_id]);

  const latestPullRun = pullRunsQuery.data?.latest ?? null;
  const pullRuns = pullRunsQuery.data?.runs ?? [];

  useEffect(() => {
    if (!latestPullRun) return;
    if (isActivePullRun(latestPullRun.status)) {
      seenRunningPullRunId.current = latestPullRun.id;
      void queryClient.invalidateQueries({ queryKey: COUNTIES_KEY });
      void queryClient.invalidateQueries({ queryKey: STATS_KEY });
      return;
    }
    if (seenRunningPullRunId.current !== latestPullRun.id) return;
    seenRunningPullRunId.current = null;
    track('County Outreach Pull All Finished', {
      duration_ms: latestPullRun.duration_ms,
      emails_found: latestPullRun.emails_found,
      emails_autofilled: latestPullRun.emails_autofilled,
      batch_size: latestPullRun.batch_size,
      state_count: latestPullRun.state_count,
      pulled_ok: latestPullRun.pulled_ok,
      pulled_error: latestPullRun.pulled_error,
    });
    void queryClient.invalidateQueries({ queryKey: COUNTIES_KEY });
    void queryClient.invalidateQueries({ queryKey: STATS_KEY });
  }, [latestPullRun, queryClient]);

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: STATS_KEY }),
      queryClient.invalidateQueries({ queryKey: COUNTIES_KEY }),
      queryClient.invalidateQueries({ queryKey: DRAFTS_KEY }),
      queryClient.invalidateQueries({ queryKey: CONTROL_KEY }),
      queryClient.invalidateQueries({ queryKey: MACROS_KEY }),
      queryClient.invalidateQueries({ queryKey: PULL_RUNS_KEY }),
    ]);
  };

  const seedMutation = useMutation({
    mutationFn: () =>
      apiJson<{ message: string }>(
        getToken,
        '/v1/admin/county-court-outreach/seed',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            refresh_urls: false,
            states: selectedState ? [selectedState] : null,
          }),
        },
      ),
    onSuccess: async (data) => {
      track('County Outreach Seeded', {
        state: selectedState || 'all',
      });
      setBanner({ ok: true, text: data.message });
      await invalidateAll();
    },
    onError: (err) => setBanner({ ok: false, text: parseDetail(err) }),
  });

  const seedAdjacentMutation = useMutation({
    mutationFn: () =>
      apiJson<{ message: string; counties_created: number; counties_updated: number }>(
        getToken,
        '/v1/admin/county-court-outreach/seed-adjacent-found',
        { method: 'POST' },
      ),
    onSuccess: async (data) => {
      track('County Outreach Adjacent Entities Seeded', {
        created: data.counties_created,
        updated: data.counties_updated,
      });
      setBanner({ ok: true, text: data.message });
      await invalidateAll();
    },
    onError: (err) => setBanner({ ok: false, text: parseDetail(err) }),
  });

  const saveCountyMutation = useMutation({
    mutationFn: () =>
      apiJson(
        getToken,
        `/v1/admin/county-court-outreach/counties/${selectedCounty?.contact_id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...edit,
            email: edit.email.trim(),
          }),
        },
      ),
    onSuccess: async () => {
      setBanner({ ok: true, text: 'County saved.' });
      await invalidateAll();
    },
    onError: (err) => setBanner({ ok: false, text: parseDetail(err) }),
  });

  const pullMutation = useMutation({
    mutationFn: () =>
      apiJson<{ message: string }>(
        getToken,
        `/v1/admin/county-court-outreach/counties/${selectedCounty?.contact_id}/pull`,
        { method: 'POST' },
      ),
    onSuccess: async (data) => {
      track('County Resource List Pulled', {
        county_name: selectedCounty?.county_name,
        state: selectedCounty?.state,
      });
      setBanner({ ok: true, text: data.message });
      await invalidateAll();
    },
    onError: (err) => setBanner({ ok: false, text: parseDetail(err) }),
  });

  const pullAllMutation = useMutation({
    mutationFn: () =>
      apiJson<{ message: string; run_id: number }>(
        getToken,
        '/v1/admin/county-court-outreach/pull-all',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            states: selectedState ? [selectedState] : null,
            skip_with_email: !rePullWithEmail,
          }),
        },
      ),
    onSuccess: async (data) => {
      track('County Outreach Pull All Queued', {
        run_id: data.run_id,
        state: selectedState || 'all',
        skip_with_email: !rePullWithEmail,
      });
      setBanner({
        ok: true,
        text: `${data.message} Progress shows in Pull runs below.`,
      });
      await queryClient.invalidateQueries({ queryKey: PULL_RUNS_KEY });
    },
    onError: (err) => setBanner({ ok: false, text: parseDetail(err) }),
  });

  const saveDraftMutation = useMutation({
    mutationFn: (status: 'draft' | 'approved') =>
      apiJson(getToken, '/v1/admin/county-court-outreach/drafts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact_id: selectedDraft?.contact_id,
          email_key: emailKey,
          subject,
          body,
          status,
        }),
      }),
    onSuccess: async (_data, status) => {
      if (status === 'approved') {
        track('County Outreach Draft Approved', {
          county_name: selectedDraft?.county_name,
          state: selectedDraft?.state,
          email_key: emailKey,
        });
      }
      setBanner({
        ok: true,
        text: status === 'approved' ? 'Draft approved.' : 'Draft saved.',
      });
      const currentIndex = drafts.findIndex(
        (row) => row.contact_id === selectedDraft?.contact_id,
      );
      const next = drafts[currentIndex + 1];
      if (next) setSelectedDraftId(next.contact_id);
      await invalidateAll();
    },
    onError: (err) => setBanner({ ok: false, text: parseDetail(err) }),
  });

  const previewDraftMutation = useMutation({
    mutationFn: (payload: {
      contact_id: number;
      subject: string;
      body: string;
    }) =>
      apiJson<PreviewResponse>(
        getToken,
        '/v1/admin/county-court-outreach/preview',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: (data) => {
      track('County Outreach Email Previewed', {
        county_name: selectedDraft?.county_name,
        email_key: emailKey,
      });
      setPreviewError(null);
      setPreviewMode('html');
      setPreviewResult(data);
    },
    onError: (e) => setPreviewError(parseDetail(e)),
  });

  const switchTopSubjectMutation = useMutation({
    mutationFn: async (row: DraftRow) => {
      const rendered = topSubject
        ? applyMacroPlaceholders(
            topSubject.template,
            countyContextForMacros(null, row, officeLabels),
          ).value.trim()
        : '';
      try {
        return await apiJson<SwitchTopSubjectResponse>(
          getToken,
          '/v1/admin/county-court-outreach/drafts/switch-top-subject',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contact_id: row.contact_id,
              email_key: emailKey,
            }),
          },
        );
      } catch (err) {
        const canFallback =
          Boolean(rendered) &&
          Boolean(row.body?.trim()) &&
          err instanceof ApiError &&
          (err.status === 404 || err.status === 422);
        if (!canFallback) throw err;
        await apiJson(getToken, '/v1/admin/county-court-outreach/drafts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_id: row.contact_id,
            email_key: emailKey,
            subject: rendered,
            body: row.body,
            status: row.email_status === 'approved' ? 'approved' : 'draft',
          }),
        });
        const alreadyUsing = subjectsMatch(row.subject, rendered);
        return {
          email_id: row.email_id ?? 0,
          contact_id: row.contact_id,
          email_key: emailKey,
          status: row.email_status === 'approved' ? 'approved' : 'draft',
          subject: rendered,
          previous_subject: row.subject,
          top_subject: topSubject as TopSubject,
          already_using: alreadyUsing,
          message: alreadyUsing
            ? 'Already using the top-replied subject'
            : 'Subject switched to the top-replied line for this touch',
        };
      }
    },
    onSuccess: async (data, row) => {
      track('County Outreach Draft Subject Switched To Top', {
        county_name: row.county_name,
        state: row.state,
        email_key: emailKey,
        already_using: data.already_using,
        reply_count: data.top_subject.reply_count,
      });
      if (selectedDraft?.contact_id === row.contact_id) {
        setSubject(data.subject);
      }
      setBanner({ ok: true, text: data.message });
      await queryClient.invalidateQueries({ queryKey: DRAFTS_KEY });
    },
    onError: (err) => setBanner({ ok: false, text: parseDetail(err) }),
  });

  const skipMutation = useMutation({
    mutationFn: () =>
      apiJson(getToken, '/v1/admin/county-court-outreach/skip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact_id: selectedDraft?.contact_id,
          email_key: emailKey,
        }),
      }),
    onSuccess: async () => {
      track('County Outreach Draft Skipped', {
        county_name: selectedDraft?.county_name,
        email_key: emailKey,
      });
      const currentIndex = drafts.findIndex(
        (row) => row.contact_id === selectedDraft?.contact_id,
      );
      const next = drafts[currentIndex + 1];
      if (next) setSelectedDraftId(next.contact_id);
      await invalidateAll();
    },
    onError: (err) => setBanner({ ok: false, text: parseDetail(err) }),
  });

  const generateMutation = useMutation({
    mutationFn: () =>
      apiJson<{ subject: string; body: string }>(
        getToken,
        '/v1/admin/county-court-outreach/generate-draft',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_id: selectedDraft?.contact_id,
            email_key: emailKey,
          }),
        },
      ),
    onSuccess: (data) => {
      setSubject(data.subject);
      setBody(data.body);
    },
    onError: (err) => setBanner({ ok: false, text: parseDetail(err) }),
  });

  const repliesQuery = useQuery({
    queryKey: COUNTY_COURT_OUTREACH_REPLIES_QUERY_KEY,
    queryFn: () =>
      apiJson<CountyCourtOutreachRepliesListResponse>(
        getToken,
        '/v1/admin/county-court-outreach/replies?limit=50',
      ),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const { hasUnread: hasUnreadReplies, markRepliesOpened } =
    useCountyCourtOutreachUnreadReplies(repliesQuery.data?.replies);

  const [openReplyId, setOpenReplyId] = useState<string | null>(null);
  const [openReplyMode, setOpenReplyMode] = useState<'html' | 'text'>('html');
  const replyDetailQuery = useQuery({
    queryKey: ['admin', 'county-court-outreach-reply', openReplyId] as const,
    queryFn: () =>
      apiJson<CountyCourtOutreachReplyDetailResponse>(
        getToken,
        `/v1/admin/county-court-outreach/replies/${encodeURIComponent(
          openReplyId ?? '',
        )}`,
      ),
    enabled: openReplyId !== null,
  });

  const sendReplyMutation = useMutation({
    mutationFn: (vars: {
      replyId: string;
      payload: CountyCourtOutreachReplySendRequest;
    }) =>
      sendOutreachReply<CountyCourtOutreachReplySendResponse>(
        getToken,
        `/v1/admin/county-court-outreach/replies/${encodeURIComponent(vars.replyId)}/send`,
        vars.payload,
      ),
    onSuccess: (_data, vars) => {
      track('County Court Outreach Reply Sent', {
        reply_id: vars.replyId,
        attachment_count: vars.payload.files.length,
      });
      queryClient.invalidateQueries({
        queryKey: COUNTY_COURT_OUTREACH_REPLIES_QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'county-court-outreach-reply', vars.replyId],
      });
      queryClient.invalidateQueries({ queryKey: DRAFTS_KEY });
    },
  });

  const neverEmailMutation = useMutation({
    mutationFn: (contactId: number) =>
      apiJson<CountyCourtOutreachNeverEmailResponse>(
        getToken,
        `/v1/admin/county-court-outreach/counties/${contactId}/never-email`,
        { method: 'POST' },
      ),
    onSuccess: async (data) => {
      track('County Court Outreach Never Email', {
        contact_id: data.contact_id,
        skipped_drafts: data.skipped_drafts,
      });
      setBanner({ ok: true, text: data.message });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: COUNTY_COURT_OUTREACH_REPLIES_QUERY_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: ['admin', 'county-court-outreach-reply', openReplyId],
        }),
        invalidateAll(),
      ]);
    },
    onError: (err) => setBanner({ ok: false, text: parseDetail(err) }),
  });

  const controlMutation = useMutation({
    mutationFn: (payload: {
      enabled?: boolean;
      paused?: boolean;
      daily_limit?: number;
    }) =>
      apiJson<{ message: string }>(
        getToken,
        '/v1/admin/county-court-outreach/control',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: async (data, payload) => {
      if (typeof payload.daily_limit === 'number') {
        track('County Outreach Daily Limit Saved', {
          daily_limit: payload.daily_limit,
        });
        setDailyLimitDraft(String(payload.daily_limit));
      }
      setBanner({ ok: true, text: data.message });
      await queryClient.invalidateQueries({ queryKey: CONTROL_KEY });
    },
    onError: (err) => setBanner({ ok: false, text: parseDetail(err) }),
  });

  const saveMacroMutation = useMutation({
    mutationFn: (payload: {
      name: string;
      description: string | null;
      email_key: string | null;
      subject: string;
      body: string;
    }) =>
      apiJson<MacroSaveResponse>(
        getToken,
        '/v1/admin/county-court-outreach/macros',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: async (data) => {
      track('County Outreach Macro Saved', {
        created: data.created,
        email_key: data.macro.email_key,
      });
      setMacroBanner({ ok: true, text: data.message });
      setMacroSaveName('');
      setMacroSaveDescription('');
      await queryClient.invalidateQueries({ queryKey: MACROS_KEY });
    },
    onError: (err) => setMacroBanner({ ok: false, text: parseDetail(err) }),
  });

  const deleteMacroMutation = useMutation({
    mutationFn: (macroId: number) =>
      apiJson<MacroDeleteResponse>(
        getToken,
        `/v1/admin/county-court-outreach/macros/${macroId}`,
        { method: 'DELETE' },
      ),
    onSuccess: async (data) => {
      track('County Outreach Macro Deleted');
      setMacroBanner({ ok: true, text: data.message });
      await queryClient.invalidateQueries({ queryKey: MACROS_KEY });
    },
    onError: (err) => setMacroBanner({ ok: false, text: parseDetail(err) }),
  });

  const officeLabels = countiesQuery.data?.office_type_labels ?? {};
  const kindLabels = countiesQuery.data?.resource_list_kind_labels ?? {};
  const stats = statsQuery.data;
  const control = controlQuery.data;
  const selectedDraftLocked = selectedDraft?.email_status === 'sent';
  const selectedDraftSourceUrl = selectedDraft
    ? draftSourceUrl(selectedDraft)
    : null;
  const macroCounty = countyContextForMacros(
    selectedCounty,
    selectedDraft,
    officeLabels,
  );

  const handleLoadMacro = (macro: MacroRow) => {
    const subjectResult = applyMacroPlaceholders(macro.subject, macroCounty);
    const bodyResult = applyMacroPlaceholders(macro.body, macroCounty);
    setSubject(subjectResult.value);
    setBody(bodyResult.value);
    const subs = [
      ...new Set([
        ...subjectResult.substitutions,
        ...bodyResult.substitutions,
      ]),
    ];
    track('County Outreach Macro Loaded', {
      email_key: macro.email_key,
      county_name: selectedDraft?.county_name,
    });
    const quotedName = JSON.stringify(macro.name);
    setMacroBanner({
      ok: true,
      text:
        subs.length > 0
          ? `Loaded macro ${quotedName}. Substituted: ${subs.join(', ')}.`
          : `Loaded macro ${quotedName}. No substitutions.`,
    });
  };

  return (
    <div className='flex flex-col gap-4 sm:gap-6'>
      <div className={gymEditorOpen ? 'hidden lg:block' : undefined}>
        <h1 className='text-xl sm:text-2xl font-semibold text-gray-900'>
          County court outreach
        </h1>
        <p className='mt-1 text-sm text-gray-600 hidden sm:block'>
          Get Harbor onto family-court self-help resource lists in every
          state. Research the PDF, find the right person, then work the
          email gym.
        </p>
      </div>

      <section
        className={`bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 space-y-3${
          gymEditorOpen ? ' hidden lg:block' : ''
        }`}
      >
        <div>
          <h2 className='text-lg font-semibold text-gray-900'>
            Leave-behind one-pagers
          </h2>
          <p className='mt-1 text-sm text-gray-600 hidden sm:block'>
            Parent-facing US Letter sheets for FCS racks. These are the latest
            committed files — attach them to clerk replies or print a stack.
          </p>
        </div>
        <div className='flex flex-wrap gap-2'>
          <a
            href={LEAVE_BEHIND_PDF.en}
            download={LEAVE_BEHIND_PDF_DOWNLOAD.en}
            onClick={() =>
              track('County Outreach Leave Behind Downloaded', {
                language: 'en',
              })
            }
            className='px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white'
          >
            Download English PDF
          </a>
          <a
            href={LEAVE_BEHIND_PDF.es}
            download={LEAVE_BEHIND_PDF_DOWNLOAD.es}
            onClick={() =>
              track('County Outreach Leave Behind Downloaded', {
                language: 'es',
              })
            }
            className='px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white'
          >
            Download Spanish PDF
          </a>
          <a
            href='/leave-behind?print=1'
            target='_blank'
            rel='noreferrer'
            className='px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50'
          >
            Preview / print English
          </a>
          <a
            href='/leave-behind/es?print=1'
            target='_blank'
            rel='noreferrer'
            className='px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50'
          >
            Preview / print Spanish
          </a>
        </div>
      </section>

      {banner ? (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            banner.ok
              ? 'bg-green-50 border-green-200 text-green-900'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {banner.text}
        </div>
      ) : null}

      <section
        className={`bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 space-y-4${
          gymEditorOpen ? ' hidden lg:block' : ''
        }`}
      >
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>Pipeline</h2>
            <p className='mt-1 text-sm text-gray-600 hidden sm:block'>
              Seed registered jurisdictions, then pull resource lists and
              fill in the self-help contact. Filter by state before seeding
              or pulling if you only want one roster. Pull All also inserts
              bar / AOC / parent-education offices linked from those pages.
            </p>
          </div>
          <div className='flex flex-wrap gap-2'>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className='px-3 py-2 text-sm border border-gray-300 rounded-md bg-white'
            >
              <option value=''>All states</option>
              {(
                statsQuery.data?.registered_states ??
                countiesQuery.data?.registered_states ??
                []
              ).map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
            <button
              type='button'
              disabled={seedMutation.isPending}
              onClick={() => seedMutation.mutate()}
              className='px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white disabled:opacity-50'
            >
              {seedMutation.isPending
                ? 'Seeding…'
                : selectedState
                  ? `Seed ${selectedState}`
                  : 'Seed all states'}
            </button>
            <button
              type='button'
              disabled={seedAdjacentMutation.isPending}
              onClick={() => seedAdjacentMutation.mutate()}
              className='px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
            >
              {seedAdjacentMutation.isPending
                ? 'Seeding adjacent…'
                : 'Seed Adjacent Found Entities'}
            </button>
            <button
              type='button'
              disabled={pullAllMutation.isPending}
              onClick={() =>
                setConfirmDialog({
                  title: selectedState
                    ? `Pull ${selectedState} resource lists?`
                    : 'Pull every resource list?',
                  message: rePullWithEmail
                    ? 'Queues a Celery job that fetches each starting URL, including contacts that already have an email. Off-page bar / AOC / parent-education links are inserted as new gym rows.'
                    : 'Queues a Celery job that fetches each starting URL. Contacts that already have an email are skipped. Off-page bar / AOC / parent-education links are inserted as new gym rows.',
                  confirmLabel: 'Queue pull-all',
                  onConfirm: () => pullAllMutation.mutate(),
                })
              }
              className='px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
            >
              Pull all lists
            </button>
            <label className='flex items-center gap-2 text-sm text-gray-600'>
              <input
                type='checkbox'
                checked={rePullWithEmail}
                onChange={(e) => setRePullWithEmail(e.target.checked)}
              />
              Re-pull contacts that already have an email
            </label>
          </div>
        </div>
        {stats ? (
          <div className='grid grid-cols-2 md:grid-cols-4 xl:grid-cols-9 gap-3'>
            {[
              ['Jurisdictions', `${stats.seeded_counties}/${stats.expected_counties ?? stats.expected_california_counties}`],
              ['Pulled', String(stats.pulled_count)],
              ['Have a list', String(stats.have_list_count)],
              ['Harbor listed', String(stats.harbor_listed_count)],
              ['Need research', String(stats.needs_research_count)],
              ['Failed pulls', String(stats.failed_pull_count ?? 0)],
              ['Need email', String(stats.needs_email_count)],
              ['Drafts', String(stats.draft_email_records)],
              ['Approved', String(stats.approved_email_records)],
            ].map(([label, value]) => (
              <div key={label} className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  {label}
                </p>
                <p className='mt-1 text-xl font-semibold tabular-nums text-gray-900'>
                  {value}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className='text-sm text-gray-500'>Loading pipeline…</p>
        )}
      </section>

      <section
        className={`bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 space-y-4${
          gymEditorOpen ? ' hidden lg:block' : ''
        }`}
      >
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <button
            type='button'
            onClick={() => {
              setResearchBoardCollapsed((prev) => {
                const next = !prev;
                writeResearchBoardCollapsed(next);
                track('County Outreach Research Board Toggled', {
                  collapsed: next,
                });
                return next;
              });
            }}
            aria-expanded={!researchBoardCollapsed}
            className='flex items-center gap-2 text-left rounded-md hover:bg-gray-50 -ml-1 px-1 py-0.5'
          >
            <svg
              aria-hidden='true'
              viewBox='0 0 20 20'
              className={`h-4 w-4 text-gray-500 transition-transform ${
                researchBoardCollapsed ? '-rotate-90' : 'rotate-0'
              }`}
              fill='currentColor'
            >
              <path d='M5.3 7.3a1 1 0 0 1 1.4 0L10 10.58l3.3-3.3a1 1 0 1 1 1.4 1.42l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4z' />
            </svg>
            <h2 className='text-lg font-semibold text-gray-900'>
              Research board
            </h2>
          </button>
          {researchBoardCollapsed ? (
            <p className='text-xs text-gray-500'>
              {counties.length
                ? `${counties.length} ${counties.length === 1 ? 'county' : 'counties'}`
                : 'Collapsed'}
            </p>
          ) : (
            <input
              value={countyQuery}
              onChange={(e) => setCountyQuery(e.target.value)}
              placeholder='Search county, person, email…'
              className='px-3 py-2 border border-gray-300 rounded-md text-sm w-full sm:w-64'
            />
          )}
        </div>
        {researchBoardCollapsed ? null : (
        <>
        <div className={hScrollPillRowClassName()}>
          <div className='flex w-max gap-2 px-1'>
          {RESEARCH_FILTERS.map((filter) => (
            <button
              key={filter.value}
              type='button'
              onClick={() => setResearchFilter(filter.value)}
              className={`px-3 py-2 sm:py-1.5 rounded-full text-xs font-semibold border whitespace-nowrap ${
                researchFilter === filter.value
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {filter.label}
            </button>
          ))}
          </div>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch'>
          <div className='h-full border border-gray-200 rounded-lg overflow-x-auto'>
            <table className='min-w-full text-sm'>
              <thead className='bg-gray-50 sticky top-0'>
                <tr>
                  <th className='text-left px-3 py-2 font-semibold text-gray-600'>
                    Jurisdiction
                  </th>
                  <th className='text-left px-3 py-2 font-semibold text-gray-600'>List</th>
                  <th className='text-left px-3 py-2 font-semibold text-gray-600'>Contact</th>
                </tr>
              </thead>
              <tbody>
                {counties.map((row) => (
                  <tr
                    key={row.contact_id}
                    onClick={() => setSelectedCountyId(row.contact_id)}
                    className={`cursor-pointer border-t border-gray-100 ${
                      selectedCounty?.contact_id === row.contact_id
                        ? 'bg-sky-50'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className='px-3 py-2'>
                      <div className='font-semibold text-gray-900'>
                        {jurisdictionLabel(row)}
                      </div>
                      <div className='text-xs text-gray-500'>
                        Tier {row.tier ?? '—'} · {row.office_type_label}
                      </div>
                    </td>
                    <td className='px-3 py-2'>
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${kindBadge(row.resource_list_kind, row.last_pull_status)}`}
                      >
                        {listKindLabel(row, kindLabels)}
                      </span>
                      <div
                        className={`text-xs mt-1 ${listColumnSubtext(row).className}`}
                      >
                        {listColumnSubtext(row).text}
                      </div>
                    </td>
                    <td className='px-3 py-2 text-xs text-gray-600'>
                      {row.contact_name || row.contact_title || '—'}
                      <div>{row.email || 'no email'}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!counties.length ? (
              <p className='p-4 text-sm text-gray-500'>
                No jurisdictions yet. Seed a state (or all states) to load the
                roster.
              </p>
            ) : null}
          </div>

          <div className='h-full border border-gray-200 rounded-lg p-4 space-y-3'>
            {selectedCounty ? (
              <>
                <div className='flex items-start justify-between gap-3'>
                  <div>
                    <h3 className='text-base font-semibold text-gray-900'>
                      {jurisdictionLabel(selectedCounty)}
                    </h3>
                    <p className='text-xs text-gray-500'>
                      Talk to the {selectedCounty.office_type_label} first.
                    </p>
                  </div>
                  <button
                    type='button'
                    disabled={pullMutation.isPending}
                    onClick={() => pullMutation.mutate()}
                    className='px-3 py-2 text-sm font-semibold rounded-md bg-primary text-white disabled:opacity-50'
                  >
                    {pullMutation.isPending ? 'Pulling…' : 'Pull PDF / page'}
                  </button>
                </div>
                <div className='flex flex-wrap gap-2 text-xs'>
                  {selectedCounty.court_website ? (
                    <a className='text-primary underline' href={selectedCounty.court_website} target='_blank' rel='noreferrer'>
                      Court site
                    </a>
                  ) : null}
                  {selectedCounty.self_help_url ? (
                    <a className='text-primary underline' href={selectedCounty.self_help_url} target='_blank' rel='noreferrer'>
                      Self-help
                    </a>
                  ) : null}
                  {selectedCounty.fcs_url ? (
                    <a className='text-primary underline' href={selectedCounty.fcs_url} target='_blank' rel='noreferrer'>
                      FCS
                    </a>
                  ) : null}
                  {selectedCounty.resource_list_url ? (
                    <a className='text-primary underline' href={selectedCounty.resource_list_url} target='_blank' rel='noreferrer'>
                      Resource list
                    </a>
                  ) : null}
                </div>
                <div
                  className={`text-xs rounded border px-2 py-2 ${
                    selectedCounty.last_pull_status === 'error'
                      ? 'bg-red-50 border-red-200 text-red-800'
                      : 'bg-gray-50 border-gray-200 text-gray-600'
                  }`}
                >
                  <div>
                    Last pull: {selectedCounty.last_pull_status || '—'} ·{' '}
                    {formatPulledAt(selectedCounty.last_pulled_at)}
                  </div>
                  {selectedCounty.last_pull_source_url ? (
                    <a
                      className='underline break-all'
                      href={selectedCounty.last_pull_source_url}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {selectedCounty.last_pull_source_url}
                    </a>
                  ) : null}
                  {selectedCounty.last_pull_status === 'error' ? (
                    <div className='mt-1'>
                      {selectedCounty.last_pull_error_short ||
                        selectedCounty.last_pull_error ||
                        'Fetch failed. Try the homepage URL or pull again.'}
                    </div>
                  ) : null}
                </div>
                {selectedCounty.last_pull_excerpt ? (
                  <p className='text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-2 max-h-24 overflow-y-auto'>
                    {selectedCounty.last_pull_excerpt}
                  </p>
                ) : null}
                {(() => {
                  const suggestions = selectedCounty.suggestions ?? EMPTY_SUGGESTIONS;
                  const hasSuggestions =
                    suggestions.emails.length ||
                    suggestions.phones.length ||
                    suggestions.contacts.length ||
                    suggestions.resource_urls.length;
                  if (!hasSuggestions) return null;
                  return (
                    <div className='rounded-lg border border-sky-200 bg-sky-50 p-3 space-y-2'>
                      <p className='text-xs font-semibold uppercase tracking-wide text-sky-800'>
                        Found on the page
                      </p>
                      {suggestions.contacts.length ? (
                        <div className='space-y-1'>
                          {suggestions.contacts.map((contact, index) => (
                            <button
                              key={`${contact.email || contact.name || 'contact'}-${index}`}
                              type='button'
                              onClick={() => {
                                track('County Outreach Suggestion Applied', {
                                  county_name: selectedCounty.county_name,
                                  field: 'contact',
                                });
                                setEdit((prev) => ({
                                  ...prev,
                                  contact_name: contact.name || prev.contact_name,
                                  contact_title: contact.title || prev.contact_title,
                                  email: contact.email || prev.email,
                                  phone: contact.phone || prev.phone,
                                }));
                              }}
                              className='block w-full text-left text-xs rounded border border-sky-200 bg-white px-2 py-1.5 hover:bg-sky-100'
                            >
                              <span className='font-semibold text-gray-900'>
                                {contact.name || contact.email || 'Suggested contact'}
                              </span>
                              <span className='text-gray-500'>
                                {contact.title ? ` · ${contact.title}` : ''}
                                {contact.email ? ` · ${contact.email}` : ''}
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : null}
                      {suggestions.emails.length ? (
                        <div className='flex flex-wrap gap-1'>
                          {suggestions.emails.map((email) => (
                            <button
                              key={email}
                              type='button'
                              onClick={() => {
                                track('County Outreach Suggestion Applied', {
                                  county_name: selectedCounty.county_name,
                                  field: 'email',
                                });
                                setEdit((prev) => ({ ...prev, email }));
                              }}
                              className='px-2 py-0.5 text-xs rounded-full border border-sky-300 bg-white text-sky-900 hover:bg-sky-100'
                            >
                              {email}
                            </button>
                          ))}
                        </div>
                      ) : null}
                      {suggestions.phones.length ? (
                        <div className='flex flex-wrap gap-1'>
                          {suggestions.phones.map((phone) => (
                            <button
                              key={phone}
                              type='button'
                              onClick={() => {
                                track('County Outreach Suggestion Applied', {
                                  county_name: selectedCounty.county_name,
                                  field: 'phone',
                                });
                                setEdit((prev) => ({ ...prev, phone }));
                              }}
                              className='px-2 py-0.5 text-xs rounded-full border border-sky-300 bg-white text-sky-900 hover:bg-sky-100'
                            >
                              {phone}
                            </button>
                          ))}
                        </div>
                      ) : null}
                      {suggestions.resource_urls.length ? (
                        <div className='space-y-1'>
                          {suggestions.resource_urls.map((url) => (
                            <div key={url} className='flex items-center gap-2'>
                              <a
                                className='text-xs text-primary underline break-all'
                                href={url}
                                target='_blank'
                                rel='noreferrer'
                              >
                                {url}
                              </a>
                              <button
                                type='button'
                                onClick={() => {
                                  track('County Outreach Suggestion Applied', {
                                    county_name: selectedCounty.county_name,
                                    field: 'resource_list_url',
                                  });
                                  setEdit((prev) => ({
                                    ...prev,
                                    resource_list_url: url,
                                  }));
                                }}
                                className='shrink-0 text-xs font-semibold text-sky-800'
                              >
                                Use
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })()}
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                  <label className='text-xs font-semibold text-gray-600'>
                    Person
                    <input
                      value={edit.contact_name}
                      onChange={(e) =>
                        setEdit((prev) => ({ ...prev, contact_name: e.target.value }))
                      }
                      className='mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-normal'
                    />
                  </label>
                  <label className='text-xs font-semibold text-gray-600'>
                    Title
                    <input
                      value={edit.contact_title}
                      onChange={(e) =>
                        setEdit((prev) => ({ ...prev, contact_title: e.target.value }))
                      }
                      className='mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-normal'
                    />
                  </label>
                  <label className='text-xs font-semibold text-gray-600'>
                    Email
                    <input
                      value={edit.email}
                      onChange={(e) =>
                        setEdit((prev) => ({ ...prev, email: e.target.value }))
                      }
                      className='mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-normal'
                    />
                  </label>
                  <label className='text-xs font-semibold text-gray-600'>
                    Phone
                    <input
                      value={edit.phone}
                      onChange={(e) =>
                        setEdit((prev) => ({ ...prev, phone: e.target.value }))
                      }
                      className='mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-normal'
                    />
                  </label>
                  <label className='text-xs font-semibold text-gray-600'>
                    Role to contact
                    <select
                      value={edit.office_type}
                      onChange={(e) =>
                        setEdit((prev) => ({ ...prev, office_type: e.target.value }))
                      }
                      className='mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-normal bg-white'
                    >
                      {Object.entries(officeLabels).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className='text-xs font-semibold text-gray-600 sm:col-span-2'>
                    Resource list URL
                    <input
                      value={edit.resource_list_url}
                      onChange={(e) =>
                        setEdit((prev) => ({
                          ...prev,
                          resource_list_url: e.target.value,
                        }))
                      }
                      className='mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-normal'
                    />
                  </label>
                  <label className='text-xs font-semibold text-gray-600 sm:col-span-2'>
                    Self-help URL
                    <input
                      value={edit.self_help_url}
                      onChange={(e) =>
                        setEdit((prev) => ({
                          ...prev,
                          self_help_url: e.target.value,
                        }))
                      }
                      className='mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-normal'
                    />
                  </label>
                  <label className='text-xs font-semibold text-gray-600 sm:col-span-2'>
                    FCS URL
                    <input
                      value={edit.fcs_url}
                      onChange={(e) =>
                        setEdit((prev) => ({
                          ...prev,
                          fcs_url: e.target.value,
                        }))
                      }
                      className='mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-normal'
                    />
                  </label>
                  <label className='text-xs font-semibold text-gray-600 sm:col-span-2'>
                    Court website
                    <input
                      value={edit.court_website}
                      onChange={(e) =>
                        setEdit((prev) => ({
                          ...prev,
                          court_website: e.target.value,
                        }))
                      }
                      className='mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-normal'
                    />
                  </label>
                  <label className='text-xs font-semibold text-gray-600 sm:col-span-2'>
                    Notes
                    <textarea
                      value={edit.notes}
                      onChange={(e) =>
                        setEdit((prev) => ({ ...prev, notes: e.target.value }))
                      }
                      rows={3}
                      className='mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-normal'
                    />
                  </label>
                </div>
                <button
                  type='button'
                  disabled={saveCountyMutation.isPending}
                  onClick={() => saveCountyMutation.mutate()}
                  className='px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
                >
                  Save county
                </button>
                {selectedCounty.recent_pulls.length ? (
                  <div className='space-y-1'>
                    <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                      Recent pulls
                    </p>
                    {selectedCounty.recent_pulls.map((pull) => (
                      <div
                        key={pull.id}
                        className='text-xs text-gray-600 border border-gray-200 rounded px-2 py-1.5'
                      >
                        <span
                          className={
                            pull.status === 'error' ? 'text-red-700' : 'text-gray-800'
                          }
                        >
                          {pull.status}
                        </span>
                        {' · '}
                        {formatPulledAt(pull.created_at)}
                        {pull.error_short || pull.error ? (
                          <div className='text-red-700'>
                            {pull.error_short || pull.error}
                          </div>
                        ) : null}
                        <a
                          className='block text-primary underline break-all'
                          href={pull.source_url}
                          target='_blank'
                          rel='noreferrer'
                        >
                          {pull.source_url}
                        </a>
                      </div>
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              <p className='text-sm text-gray-500'>Select a county to research it.</p>
            )}
          </div>
        </div>
        </>
        )}
      </section>

      <section className='order-first lg:order-none bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 space-y-4'>
        <div className={gymEditorOpen ? 'hidden lg:block' : undefined}>
          <h2 className='text-lg font-semibold text-gray-900'>Email gym</h2>
          <p className='mt-1 text-sm text-gray-600 hidden sm:block'>
            Every seeded state with an email shows in Needs review. Save &
            Approve a draft; Celery sends it, at most the daily cap in Sender
            control. After approve/skip the next jurisdiction is selected.
          </p>
        </div>
        {gymEditorOpen ? (
          <div className='lg:hidden flex items-center gap-2'>
            <button
              type='button'
              onClick={() => {
                track('County Outreach Email Gym Back To List', {
                  county_name: selectedDraft?.county_name,
                  email_key: emailKey,
                });
                setGymEditorOpen(false);
              }}
              className='inline-flex items-center gap-1.5 min-h-11 px-3 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50'
            >
              ← Queue
            </button>
            <h2 className='min-w-0 truncate text-base font-semibold text-gray-900'>
              {selectedDraft
                ? jurisdictionLabel(selectedDraft)
                : 'Email gym'}
            </h2>
          </div>
        ) : null}
        <div className={gymEditorOpen ? 'hidden lg:block space-y-3' : 'space-y-3'}>
          <div className={hScrollPillRowClassName()}>
            <div className='flex w-max gap-2 px-1'>
              {['touch_1', 'touch_2', 'touch_3'].map((key) => (
                <button
                  key={key}
                  type='button'
                  onClick={() => setEmailKey(key)}
                  className={`px-3 py-2 sm:py-1.5 rounded-full text-xs font-semibold border whitespace-nowrap ${
                    emailKey === key
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-700 border-gray-300'
                  }`}
                >
                  {TOUCH_LABELS[key] ?? key}
                </button>
              ))}
            </div>
          </div>
          <div className={hScrollPillRowClassName()}>
            <div className='flex w-max gap-2 px-1'>
              {STATUS_FILTERS.filter(
                (filter) => filter.value !== 'due' || emailKey !== 'touch_1',
              ).map((filter) => (
                <button
                  key={filter.value}
                  type='button'
                  onClick={() => setDraftStatus(filter.value)}
                  className={`px-3 py-2 sm:py-1.5 rounded-full text-xs font-semibold border whitespace-nowrap ${
                    draftStatus === filter.value
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-700 border-gray-300'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-[minmax(7.5rem,9rem)_1fr] gap-2'>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className='min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white'
              aria-label='Filter email gym by state'
            >
              <option value=''>All states</option>
              {(
                statsQuery.data?.registered_states ??
                countiesQuery.data?.registered_states ??
                []
              ).map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
            <input
              value={draftQuery}
              onChange={(e) => setDraftQuery(e.target.value)}
              placeholder='Search drafts…'
              className='min-w-0 w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
          </div>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch'>
          <div
            className={`${
              gymEditorOpen ? 'hidden lg:block' : 'block'
            } h-full border border-gray-200 rounded-lg overflow-hidden`}
          >
            {drafts.map((row) => {
              const badge = draftStatusBadge(row.email_status);
              const renderedTopSubject = topSubject
                ? applyMacroPlaceholders(
                    topSubject.template,
                    countyContextForMacros(null, row, officeLabels),
                  ).value
                : '';
              const canSwitch = canSwitchDraftSubject(row.email_status);
              const alreadyUsingTop =
                Boolean(renderedTopSubject) &&
                subjectsMatch(row.subject, renderedTopSubject);
              const switchingThis =
                switchTopSubjectMutation.isPending &&
                switchTopSubjectMutation.variables?.contact_id ===
                  row.contact_id;
              const switchDisabled =
                !topSubject ||
                !renderedTopSubject ||
                !row.body?.trim() ||
                alreadyUsingTop ||
                switchTopSubjectMutation.isPending;
              const switchTitle = !topSubject
                ? 'No subject available for this touch yet'
                : !row.body?.trim()
                  ? 'Save a draft body before switching the subject'
                  : alreadyUsingTop
                    ? `Already using the top-replied subject (${topSubject.reply_count} ${
                        topSubject.reply_count === 1 ? 'reply' : 'replies'
                      })`
                    : `Switch subject to the top-replied ${
                        TOUCH_LABELS[emailKey] ?? emailKey
                      } line (${topSubject.reply_count} ${
                        topSubject.reply_count === 1 ? 'reply' : 'replies'
                      }): ${renderedTopSubject}`;
              return (
                <div
                  key={row.contact_id}
                  role='button'
                  tabIndex={0}
                  onClick={() => {
                    setSelectedDraftId(row.contact_id);
                    setGymEditorOpen(true);
                    track('County Outreach Email Gym Draft Opened', {
                      contact_id: row.contact_id,
                      county_name: row.county_name,
                      email_key: emailKey,
                    });
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    setSelectedDraftId(row.contact_id);
                    setGymEditorOpen(true);
                    track('County Outreach Email Gym Draft Opened', {
                      contact_id: row.contact_id,
                      county_name: row.county_name,
                      email_key: emailKey,
                    });
                  }}
                  className={`w-full text-left px-3 py-3 border-b border-gray-100 min-h-11 cursor-pointer ${
                    selectedDraft?.contact_id === row.contact_id
                      ? 'bg-sky-50'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className='flex items-start justify-between gap-2'>
                    <div className='min-w-0'>
                      <div className='font-semibold text-sm text-gray-900 truncate'>
                        {jurisdictionLabel(row)}
                      </div>
                      <div className='text-xs text-gray-500 truncate'>
                        {row.contact_name || row.contact_title || row.office_type}
                      </div>
                      <div className='text-xs text-gray-500 truncate'>
                        {row.email}
                      </div>
                      {row.subject ? (
                        <div
                          className='text-xs text-gray-400 truncate'
                          title={row.subject}
                        >
                          {row.subject}
                        </div>
                      ) : null}
                    </div>
                    <div className='shrink-0 flex flex-col items-end gap-1.5'>
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                      {canSwitch && alreadyUsingTop ? (
                        <span
                          className='inline-flex px-2 py-1 text-[11px] font-semibold rounded-md border border-emerald-200 bg-emerald-50 text-emerald-800 whitespace-nowrap'
                          title={switchTitle}
                        >
                          Using top subject
                        </span>
                      ) : canSwitch ? (
                        <button
                          type='button'
                          disabled={switchDisabled}
                          title={switchTitle}
                          onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            if (switchDisabled) return;
                            switchTopSubjectMutation.mutate(row);
                          }}
                          className='px-2 py-1 text-[11px] font-semibold rounded-md border border-primary text-primary hover:bg-harbor-50 disabled:opacity-50 disabled:hover:bg-transparent whitespace-nowrap'
                        >
                          {switchingThis
                            ? 'Switching…'
                            : 'Switch to Top Subject'}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
            {!drafts.length ? (
              <p className='p-4 text-sm text-gray-500'>
                Nothing in this queue. Add emails on the research board first.
              </p>
            ) : null}
          </div>

          <div
            className={`${
              gymEditorOpen ? 'block' : 'hidden'
            } lg:block h-full border border-gray-200 rounded-lg p-3 sm:p-4 space-y-3${
              gymEditorOpen ? ' pb-28 lg:pb-4' : ''
            }`}
          >
            {selectedDraft ? (
              <>
                <div>
                  <h3 className='hidden lg:block font-semibold text-gray-900'>
                    {jurisdictionLabel(selectedDraft)}
                  </h3>
                  <p className='text-xs text-gray-500'>
                    {selectedDraft.listed_apps.length
                      ? `Already lists: ${selectedDraft.listed_apps.join(', ')}`
                      : 'No apps detected yet'}
                  </p>
                  {selectedDraftSourceUrl ? (
                    <p className='mt-1 text-xs'>
                      <span className='text-gray-500'>
                        {selectedDraft.last_pull_source_url
                          ? 'Page this email came from: '
                          : 'County page: '}
                      </span>
                      <a
                        className='text-primary underline break-all'
                        href={selectedDraftSourceUrl}
                        target='_blank'
                        rel='noreferrer'
                        onClick={() =>
                          track('County Outreach Source Page Opened', {
                            county_name: selectedDraft.county_name,
                            source: selectedDraft.last_pull_source_url
                              ? 'last_pull'
                              : 'fallback',
                          })
                        }
                      >
                        {selectedDraftSourceUrl}
                      </a>
                    </p>
                  ) : (
                    <p className='mt-1 text-xs text-gray-400'>
                      No source page stored for this county yet.
                    </p>
                  )}
                </div>
                <details
                  className='rounded-lg border border-gray-200 p-3 space-y-3'
                  {...(isLg ? { open: true } : {})}
                >
                  <summary className='cursor-pointer list-none text-sm font-semibold text-gray-900 lg:pointer-events-none'>
                    Macros
                    <span className='ml-2 text-xs font-normal text-gray-500 lg:hidden'>
                      tap to expand
                    </span>
                  </summary>
                  <div className='flex items-baseline justify-between gap-2'>
                    <div>
                      <p className='hidden lg:block text-sm font-semibold text-gray-900'>
                        Macros
                      </p>
                      <p className='text-xs text-gray-500 hidden sm:block'>
                        Reusable subject + body templates. Supports{' '}
                        <code className='bg-gray-100 px-1 rounded'>
                          {'{{jurisdiction_name}}'}
                        </code>
                        ,{' '}
                        <code className='bg-gray-100 px-1 rounded'>
                          {'{{state_name}}'}
                        </code>
                        ,{' '}
                        <code className='bg-gray-100 px-1 rounded'>
                          {'{{office_label}}'}
                        </code>
                        ,{' '}
                        <code className='bg-gray-100 px-1 rounded'>
                          {'{{first_name}}'}
                        </code>
                        ,{' '}
                        <code className='bg-gray-100 px-1 rounded'>
                          {'{{contact_name}}'}
                        </code>
                        , and{' '}
                        <code className='bg-gray-100 px-1 rounded'>
                          {'{{contact_title}}'}
                        </code>
                        .
                      </p>
                    </div>
                    {macrosQuery.isFetching ? (
                      <span className='text-xs text-gray-400'>Loading...</span>
                    ) : null}
                  </div>

                  {macroBanner ? (
                    <div
                      className={`rounded-md border px-3 py-2 text-xs ${
                        macroBanner.ok
                          ? 'bg-green-50 border-green-200 text-green-900'
                          : 'bg-red-50 border-red-200 text-red-800'
                      }`}
                    >
                      {macroBanner.text}
                    </div>
                  ) : null}

                  {macrosQuery.data && macrosQuery.data.macros.length > 0 ? (
                    <ul className='space-y-1.5'>
                      {macrosQuery.data.macros.map((macro) => {
                        const scopeLabel =
                          macro.email_key === null
                            ? 'Any touch'
                            : TOUCH_LABELS[macro.email_key] ?? macro.email_key;
                        return (
                          <li
                            key={macro.id}
                            className='flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5'
                          >
                            <div className='flex-1 min-w-0'>
                              <div className='flex flex-wrap items-baseline gap-2'>
                                <span className='text-sm font-semibold text-gray-900 truncate'>
                                  {macro.name}
                                </span>
                                <span className='inline-flex px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-700 text-[10px] font-semibold uppercase tracking-wide'>
                                  {scopeLabel}
                                </span>
                                <span
                                  className={`text-[11px] tabular-nums ${
                                    (macro.reply_count ?? 0) > 0
                                      ? 'text-green-700 font-semibold'
                                      : 'text-gray-500'
                                  }`}
                                  title='Replies from contacts who received this subject, out of sent emails that match this macro'
                                >
                                  {macro.reply_count ?? 0}{' '}
                                  {(macro.reply_count ?? 0) === 1
                                    ? 'reply'
                                    : 'replies'}
                                  {' · '}
                                  {macro.sent_count ?? 0} sent
                                </span>
                              </div>
                              {macro.description ? (
                                <p className='text-xs text-gray-600 mt-0.5'>
                                  {macro.description}
                                </p>
                              ) : null}
                              <p className='text-[11px] text-gray-400 mt-0.5 truncate'>
                                {macro.subject || '(no subject)'}
                              </p>
                            </div>
                            <div className='flex gap-1 shrink-0'>
                              <button
                                type='button'
                                disabled={selectedDraftLocked}
                                onClick={() => handleLoadMacro(macro)}
                                className='px-2 py-1 text-xs font-semibold rounded border border-primary text-primary hover:bg-harbor-50 disabled:opacity-50 disabled:cursor-not-allowed'
                                title={
                                  selectedDraftLocked
                                    ? 'Editor is locked (already sent)'
                                    : 'Load into editor'
                                }
                              >
                                Load
                              </button>
                              <button
                                type='button'
                                disabled={
                                  deleteMacroMutation.isPending &&
                                  deleteMacroMutation.variables === macro.id
                                }
                                onClick={() => {
                                  setConfirmDialog({
                                    title: 'Delete macro',
                                    message: `Delete macro "${macro.name}"? This cannot be undone.`,
                                    confirmLabel: 'Delete',
                                    destructive: true,
                                    onConfirm: () =>
                                      deleteMacroMutation.mutate(macro.id),
                                  });
                                }}
                                className='px-2 py-1 text-xs font-semibold rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50'
                                title='Delete macro'
                              >
                                ×
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className='text-xs text-gray-500 italic'>
                      No macros saved yet. Save the current draft below to reuse
                      it later.
                    </p>
                  )}

                  <details className='group'>
                    <summary className='cursor-pointer list-none text-xs font-semibold text-primary hover:underline'>
                      <span className='group-open:hidden'>
                        + Save current draft as macro
                      </span>
                      <span className='hidden group-open:inline'>
                        − Hide save form
                      </span>
                    </summary>
                    <form
                      className='mt-2 space-y-2'
                      onSubmit={(e) => {
                        e.preventDefault();
                        const trimmedName = macroSaveName.trim();
                        if (!trimmedName) {
                          setMacroBanner({
                            ok: false,
                            text: 'Macro name is required.',
                          });
                          return;
                        }
                        if (!subject.trim() || !body.trim()) {
                          setMacroBanner({
                            ok: false,
                            text:
                              'Subject and body must both have content before saving as a macro.',
                          });
                          return;
                        }
                        setMacroBanner(null);
                        saveMacroMutation.mutate({
                          name: trimmedName,
                          description: macroSaveDescription.trim() || null,
                          email_key: macroSaveScopeToTouch ? emailKey : null,
                          subject,
                          body,
                        });
                      }}
                    >
                      <div>
                        <label className='block text-[11px] font-semibold text-gray-600 mb-1'>
                          Macro name (used as the unique key; saving with
                          the same name overwrites)
                        </label>
                        <input
                          value={macroSaveName}
                          onChange={(e) => setMacroSaveName(e.target.value)}
                          placeholder='e.g. Touch 2 - leave-behind'
                          className='w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm'
                        />
                      </div>
                      <div>
                        <label className='block text-[11px] font-semibold text-gray-600 mb-1'>
                          Description (optional)
                        </label>
                        <input
                          value={macroSaveDescription}
                          onChange={(e) =>
                            setMacroSaveDescription(e.target.value)
                          }
                          placeholder='When to use this macro'
                          className='w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm'
                        />
                      </div>
                      <label className='flex items-start gap-2 text-xs text-gray-700'>
                        <input
                          type='checkbox'
                          checked={macroSaveScopeToTouch}
                          onChange={(e) =>
                            setMacroSaveScopeToTouch(e.target.checked)
                          }
                          className='mt-0.5 h-4 w-4 rounded border-gray-300 text-primary'
                        />
                        <span>
                          Tag as{' '}
                          <strong>{TOUCH_LABELS[emailKey] ?? emailKey}</strong>{' '}
                          (optional label only; macros work on any touch)
                        </span>
                      </label>
                      <button
                        type='submit'
                        disabled={saveMacroMutation.isPending}
                        className='px-3 py-1.5 text-xs font-semibold rounded-md border border-primary text-primary hover:bg-harbor-50 disabled:opacity-50'
                      >
                        {saveMacroMutation.isPending
                          ? 'Saving...'
                          : 'Save macro'}
                      </button>
                    </form>
                  </details>
                </details>
                <div>
                  <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                    To
                  </p>
                  {selectedDraft.email ? (
                    <a
                      href={`mailto:${selectedDraft.email}`}
                      className='mt-0.5 block text-sm text-gray-900 break-all hover:text-primary'
                    >
                      {selectedDraft.email}
                    </a>
                  ) : (
                    <p className='mt-0.5 text-sm text-amber-700'>
                      No email on file (would skip at send time)
                    </p>
                  )}
                </div>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder='Subject'
                  className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
                />
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
                />
                <p className='text-xs text-gray-500 hidden sm:block'>
                  URLs and emails are auto-linked. Use markdown{' '}
                  <code className='bg-gray-100 px-1 rounded'>
                    [Harbor](https://harborcoparent.com)
                  </code>{' '}
                  when you want custom link text — Preview opens on HTML so
                  you can click the link.
                </p>
                <div className='flex flex-wrap gap-2'>
                  <button
                    type='button'
                    disabled={generateMutation.isPending || !selectedDraft.email}
                    onClick={() => generateMutation.mutate()}
                    className='px-3 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
                  >
                    {generateMutation.isPending ? 'Generating…' : 'Generate draft'}
                  </button>
                  <button
                    type='button'
                    disabled={saveDraftMutation.isPending}
                    onClick={() => saveDraftMutation.mutate('draft')}
                    className='px-3 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
                  >
                    Save draft
                  </button>
                  <button
                    type='button'
                    disabled={
                      previewDraftMutation.isPending ||
                      !subject.trim() ||
                      !body.trim()
                    }
                    onClick={() => {
                      if (!selectedDraft) return;
                      setPreviewError(null);
                      setPreviewMode('html');
                      previewDraftMutation.mutate({
                        contact_id: selectedDraft.contact_id,
                        subject,
                        body,
                      });
                    }}
                    className='px-3 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
                    title='Render the email exactly as the recipient will see it, including clickable markdown links.'
                  >
                    {previewDraftMutation.isPending ? 'Rendering…' : 'Preview'}
                  </button>
                  <button
                    type='button'
                    disabled={saveDraftMutation.isPending || !selectedDraft.email}
                    onClick={() => saveDraftMutation.mutate('approved')}
                    className='hidden lg:inline-flex px-3 py-2 text-sm font-semibold rounded-md bg-primary text-white disabled:opacity-50'
                    title='Save the current subject and body, then mark this draft approved for Celery.'
                  >
                    Save & Approve
                  </button>
                  <button
                    type='button'
                    disabled={skipMutation.isPending}
                    onClick={() => skipMutation.mutate()}
                    className='hidden lg:inline-flex px-3 py-2 text-sm font-semibold rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50'
                  >
                    Skip
                  </button>
                </div>
                {gymEditorOpen ? (
                  <div className='lg:hidden fixed inset-x-0 bottom-0 z-20 border-t border-gray-200 bg-white/95 backdrop-blur px-3 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex gap-2'>
                    <button
                      type='button'
                      disabled={skipMutation.isPending}
                      onClick={() => skipMutation.mutate()}
                      className='flex-1 min-h-11 px-3 py-2 text-sm font-semibold rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50'
                    >
                      Skip
                    </button>
                    <button
                      type='button'
                      disabled={
                        saveDraftMutation.isPending || !selectedDraft.email
                      }
                      onClick={() => saveDraftMutation.mutate('approved')}
                      className='flex-1 min-h-11 px-3 py-2 text-sm font-semibold rounded-md bg-primary text-white disabled:opacity-50'
                    >
                      Save & Approve
                    </button>
                  </div>
                ) : null}
                {previewError ? (
                  <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                    Preview failed: {previewError}
                  </div>
                ) : null}
              </>
            ) : (
              <p className='text-sm text-gray-500'>No draft selected.</p>
            )}
          </div>
        </div>
      </section>

      <section
        className={`bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 space-y-3${
          gymEditorOpen ? ' hidden lg:block' : ''
        }`}
      >
        <h2 className='text-lg font-semibold text-gray-900'>Sender control</h2>
        {control ? (
          <>
            <p className='text-sm text-gray-600'>
              {control.enabled ? 'Enabled' : 'Disabled'} via {control.enabled_source}
              {control.paused ? ' · paused' : ''} · {control.approved_count} approved
              · {control.sent_today} sent today · cap {control.daily_limit}/day (
              {dailyLimitSourceLabel(control.daily_limit_source)}) ·{' '}
              {control.schedule_description}
            </p>
            <div className='flex flex-wrap gap-2'>
              <button
                type='button'
                disabled={!control.redis_configured || controlMutation.isPending}
                onClick={() =>
                  controlMutation.mutate({ enabled: !control.enabled })
                }
                className='px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
              >
                {control.enabled ? 'Disable sender' : 'Enable sender'}
              </button>
              <button
                type='button'
                disabled={!control.redis_configured || controlMutation.isPending}
                onClick={() => controlMutation.mutate({ paused: !control.paused })}
                className='px-4 py-2 text-sm font-semibold rounded-md border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50'
              >
                {control.paused ? 'Resume' : 'Pause'}
              </button>
            </div>
            <div className='rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2'>
              <label
                htmlFor='county-court-daily-limit'
                className='block text-xs font-semibold uppercase tracking-wide text-gray-500'
              >
                Daily send cap
              </label>
              <div className='flex flex-wrap items-center gap-2'>
                <input
                  id='county-court-daily-limit'
                  type='number'
                  min={DAILY_LIMIT_MIN}
                  max={DAILY_LIMIT_MAX}
                  step={1}
                  value={dailyLimitDraft || String(control.daily_limit)}
                  onChange={(event) => setDailyLimitDraft(event.target.value)}
                  disabled={!control.redis_configured || controlMutation.isPending}
                  className='w-24 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white disabled:opacity-50'
                />
                <button
                  type='button'
                  disabled={
                    !control.redis_configured ||
                    controlMutation.isPending ||
                    !canSaveDailyLimit(dailyLimitDraft, control.daily_limit)
                  }
                  onClick={() => {
                    const next = parseDailyLimitInput(dailyLimitDraft);
                    if (next == null) return;
                    controlMutation.mutate({ daily_limit: next });
                  }}
                  className='px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white disabled:opacity-50'
                >
                  Save cap
                </button>
              </div>
              <p className='text-xs text-gray-500'>
                {control.sent_today} of {control.daily_limit} sent today
                (Pacific). Celery stops at this cap. {DAILY_LIMIT_MIN}–
                {DAILY_LIMIT_MAX} emails per day.
                {!control.redis_configured
                  ? ' Set REDIS_URL to change the cap from this card.'
                  : ''}
              </p>
            </div>
          </>
        ) : (
          <p className='text-sm text-gray-500'>Loading sender state…</p>
        )}
      </section>

      <section
        className={`bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4${
          gymEditorOpen ? ' hidden lg:block' : ''
        }`}
      >
        <details
          className='group'
          onToggle={(event) => {
            if (event.currentTarget.open) {
              markRepliesOpened();
            }
          }}
        >
          <summary className='cursor-pointer list-none flex items-start justify-between gap-3'>
            <div>
              <h2 className='text-lg font-semibold text-gray-900 inline-flex items-center gap-2'>
                Replies
                {hasUnreadReplies ? (
                  <AdminUnreadDot label='New replies since you last opened Replies' />
                ) : null}
                {repliesQuery.data ? (
                  <span className='ml-2 text-sm font-normal text-gray-500'>
                    ({repliesQuery.data.replies.length}
                    {repliesQuery.data.has_more ? '+' : ''})
                  </span>
                ) : null}
              </h2>
              <p className='mt-1 text-sm text-gray-600'>
                Clerk replies from the shared Resend inbox. Coach and lawyer
                replies are filtered out.
              </p>
            </div>
            <span className='shrink-0 text-xs font-semibold text-gray-500 group-open:hidden'>
              + Expand
            </span>
            <span className='shrink-0 text-xs font-semibold text-gray-500 hidden group-open:inline'>
              − Collapse
            </span>
          </summary>

          <div className='mt-4 space-y-3'>
            <div className='flex items-center justify-between gap-2'>
              <p className='text-xs text-gray-500'>
                {repliesQuery.data?.fetched_at ? (
                  <>
                    Fetched {formatRelative(repliesQuery.data.fetched_at)} from
                    Resend
                  </>
                ) : (
                  'Not fetched yet'
                )}
              </p>
              <button
                type='button'
                onClick={() => repliesQuery.refetch()}
                disabled={repliesQuery.isFetching}
                className='px-2.5 py-1 text-xs font-semibold rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
              >
                {repliesQuery.isFetching ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {repliesQuery.isError ? (
              <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                Couldn&apos;t load replies: {parseDetail(repliesQuery.error)}
              </div>
            ) : null}
            {repliesQuery.data?.error ? (
              <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                Resend returned an error: {repliesQuery.data.error}
              </div>
            ) : null}

            {repliesQuery.isLoading ? (
              <p className='text-sm text-gray-500'>Loading...</p>
            ) : repliesQuery.data &&
              repliesQuery.data.replies.length === 0 &&
              !repliesQuery.data.error ? (
              <p className='text-sm text-gray-500 italic'>
                No clerk replies yet. Once a county office replies to one of
                your sends, Resend will surface it here.
              </p>
            ) : repliesQuery.data ? (
              <ul className='divide-y divide-gray-200 border border-gray-200 rounded-md overflow-hidden'>
                {repliesQuery.data.replies.map((reply) => {
                  const matched = reply.matched_contact;
                  const youReplied = Boolean(reply.manual_reply_sent_at);
                  return (
                    <li key={reply.id}>
                      <button
                        type='button'
                        onClick={() => {
                          track('County Court Outreach Reply Opened', {
                            reply_id: reply.id,
                          });
                          setOpenReplyMode('html');
                          setOpenReplyId(reply.id);
                        }}
                        className={`w-full text-left px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:gap-3 ${
                          youReplied
                            ? 'bg-blue-50/80 hover:bg-blue-50'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className='min-w-0 flex-1'>
                          <div className='flex flex-wrap items-baseline gap-2'>
                            <span className='text-sm font-semibold text-gray-900 truncate'>
                              {reply.from_email}
                            </span>
                            {youReplied ? (
                              <span
                                className='inline-flex px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-900 text-[10px] font-semibold uppercase tracking-wide'
                                title={
                                  reply.manual_reply_sent_at
                                    ? `You replied ${formatTimestamp(reply.manual_reply_sent_at) ?? ''}`
                                    : 'You already sent a manual reply'
                                }
                              >
                                You replied
                              </span>
                            ) : null}
                            {matched ? (
                              <span
                                className='inline-flex px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 text-[10px] font-semibold uppercase tracking-wide'
                                title={`Matched county_court_outreach_contacts.id=${matched.id}, status=${matched.status}`}
                              >
                                ✓ {matchedCountyLabel(matched)}
                              </span>
                            ) : (
                              <span className='inline-flex px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-semibold uppercase tracking-wide'>
                                no match
                              </span>
                            )}
                            {reply.has_attachments ? (
                              <span className='inline-flex px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wide'>
                                📎
                              </span>
                            ) : null}
                          </div>
                          <p className='text-sm text-gray-700 truncate'>
                            {reply.subject || '(no subject)'}
                          </p>
                          <p className='text-[11px] text-gray-400 truncate'>
                            to {reply.to_emails.join(', ') || '—'}
                          </p>
                        </div>
                        <div className='shrink-0 text-right text-[11px] text-gray-500'>
                          {youReplied && reply.manual_reply_sent_at ? (
                            <div
                              className='text-blue-700 font-medium'
                              title={
                                formatTimestamp(reply.manual_reply_sent_at) ??
                                ''
                              }
                            >
                              Replied{' '}
                              {formatRelative(reply.manual_reply_sent_at)}
                            </div>
                          ) : null}
                          <div>{formatRelative(reply.received_at)}</div>
                          {reply.received_at ? (
                            <div
                              className='text-gray-400'
                              title={formatTimestamp(reply.received_at) ?? ''}
                            >
                              {new Date(reply.received_at).toLocaleString()}
                            </div>
                          ) : null}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        </details>
      </section>

      <section
        className={`bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 space-y-3${
          gymEditorOpen ? ' hidden lg:block' : ''
        }`}
      >
        <div className='flex items-start justify-between gap-3'>
          <h2 className='text-lg font-semibold text-gray-900'>Pull runs</h2>
          {latestPullRun ? (
            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                latestPullRun.status === 'ok'
                  ? 'border-green-200 bg-green-50 text-green-800'
                  : latestPullRun.status === 'error'
                    ? 'border-red-200 bg-red-50 text-red-800'
                    : 'border-amber-200 bg-amber-50 text-amber-800'
              }`}
            >
              {pullRunStatusLabel(latestPullRun)}
            </span>
          ) : null}
        </div>
        {latestPullRun ? (
          <>
            <p className='text-sm text-gray-600'>
              {latestPullRun.status === 'queued'
                ? 'Waiting for the worker to pick up the sweep.'
                : latestPullRun.status === 'running'
                  ? 'Sweep in progress. This card refreshes every few seconds.'
                  : latestPullRun.status === 'error'
                    ? latestPullRun.error || 'Sweep failed.'
                    : `Finished ${formatPulledAt(latestPullRun.finished_at)}.`}
            </p>
            <dl className='grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm'>
              <div>
                <dt className='text-xs uppercase tracking-wide text-gray-500'>
                  Duration
                </dt>
                <dd className='font-semibold text-gray-900'>
                  {isActivePullRun(latestPullRun.status)
                    ? 'In progress'
                    : formatDurationMs(latestPullRun.duration_ms)}
                </dd>
              </div>
              <div>
                <dt className='text-xs uppercase tracking-wide text-gray-500'>
                  Batch
                </dt>
                <dd className='font-semibold text-gray-900'>
                  {latestPullRun.batch_size}{' '}
                  {latestPullRun.batch_size === 1 ? 'county' : 'counties'}
                </dd>
              </div>
              <div>
                <dt className='text-xs uppercase tracking-wide text-gray-500'>
                  States
                </dt>
                <dd className='font-semibold text-gray-900'>
                  {latestPullRun.state_count}
                  {latestPullRun.states ? ` · ${latestPullRun.states}` : ''}
                </dd>
              </div>
              <div>
                <dt className='text-xs uppercase tracking-wide text-gray-500'>
                  Emails found
                </dt>
                <dd className='font-semibold text-gray-900'>
                  {latestPullRun.emails_found}
                  {latestPullRun.emails_autofilled
                    ? ` · ${latestPullRun.emails_autofilled} filled`
                    : ''}
                </dd>
              </div>
              <div>
                <dt className='text-xs uppercase tracking-wide text-gray-500'>
                  Pulls
                </dt>
                <dd className='font-semibold text-gray-900'>
                  {latestPullRun.pulled_ok} ok · {latestPullRun.pulled_error}{' '}
                  failed · {latestPullRun.skipped_no_url} skipped
                </dd>
              </div>
              <div>
                <dt className='text-xs uppercase tracking-wide text-gray-500'>
                  Lists
                </dt>
                <dd className='font-semibold text-gray-900'>
                  {latestPullRun.apps_found} with apps ·{' '}
                  {latestPullRun.harbor_listed} Harbor
                </dd>
              </div>
            </dl>
            {pullRuns.length > 1 ? (
              <div className='border-t border-gray-100 pt-3 space-y-1'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Recent
                </p>
                <ul className='space-y-1'>
                  {pullRuns.slice(1).map((run) => (
                    <li
                      key={run.id}
                      className='text-xs text-gray-600 flex flex-wrap gap-x-2'
                    >
                      <span className='font-semibold text-gray-800'>
                        {pullRunStatusLabel(run)}
                      </span>
                      <span>
                        {run.emails_found} emails · {run.batch_size} counties ·{' '}
                        {run.state_count}{' '}
                        {run.state_count === 1 ? 'state' : 'states'}
                      </span>
                      <span>
                        {formatPulledAt(run.finished_at || run.created_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : (
          <p className='text-sm text-gray-500'>
            No pull-all runs yet. Queue one from the pipeline card.
          </p>
        )}
      </section>

      {confirmDialog ? (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
          <div className='bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-4'>
            <h3 className='text-lg font-semibold text-gray-900'>{confirmDialog.title}</h3>
            <p className='text-sm text-gray-600'>{confirmDialog.message}</p>
            <div className='flex flex-col-reverse sm:flex-row sm:justify-end gap-2'>
              <button
                type='button'
                onClick={() => setConfirmDialog(null)}
                className='w-full sm:w-auto min-h-11 sm:min-h-0 px-3 py-2 text-sm font-semibold rounded-md border border-gray-300'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={() => {
                  const action = confirmDialog.onConfirm;
                  setConfirmDialog(null);
                  action();
                }}
                className={`w-full sm:w-auto min-h-11 sm:min-h-0 px-3 py-2 text-sm font-semibold rounded-md ${
                  confirmDialog.destructive
                    ? 'bg-red-600 text-white'
                    : 'bg-primary text-white'
                }`}
              >
                {confirmDialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <PreviewDialog
        result={previewResult}
        mode={previewMode}
        onModeChange={setPreviewMode}
        onClose={() => setPreviewResult(null)}
      />
      <ReplyDialog
        open={openReplyId !== null}
        replyId={openReplyId}
        loading={replyDetailQuery.isLoading}
        error={
          replyDetailQuery.isError ? parseDetail(replyDetailQuery.error) : null
        }
        reply={replyDetailQuery.data ?? null}
        mode={openReplyMode}
        onModeChange={setOpenReplyMode}
        onClose={() => {
          setOpenReplyId(null);
          sendReplyMutation.reset();
          neverEmailMutation.reset();
        }}
        sendReplyMutation={sendReplyMutation}
        neverEmailMutation={neverEmailMutation}
      />
    </div>
  );
}

const PREVIEW_URL_RE = /(https?:\/\/[^\s<>"]+)/g;

function previewHtmlSrcDoc(html: string): string {
  // Sandboxed srcdoc iframes swallow in-place navigation. Force links into a
  // new tab so markdown [label](url) anchors are actually clickable.
  if (html.includes('<head>')) {
    return html.replace('<head>', '<head><base target="_blank">');
  }
  return html;
}

function PlainTextPreview({ text }: { text: string }) {
  const nodes: ReactNode[] = [];
  const re = new RegExp(PREVIEW_URL_RE.source, 'g');
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }
    const href = match[1];
    nodes.push(
      <a
        key={`url-${key++}`}
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        className='text-primary underline'
      >
        {href}
      </a>
    );
    last = match.index + href.length;
  }
  if (last < text.length) {
    nodes.push(text.slice(last));
  }
  return (
    <pre className='w-full bg-white rounded shadow border border-gray-200 p-4 text-sm font-mono whitespace-pre-wrap text-gray-800'>
      {nodes}
    </pre>
  );
}

function ReplyDialog({
  open,
  replyId,
  loading,
  error,
  reply,
  mode,
  onModeChange,
  onClose,
  sendReplyMutation,
  neverEmailMutation,
}: {
  open: boolean;
  replyId: string | null;
  loading: boolean;
  error: string | null;
  reply: CountyCourtOutreachReplyDetailResponse | null;
  mode: 'html' | 'text';
  onModeChange: (m: 'html' | 'text') => void;
  onClose: () => void;
  sendReplyMutation: UseMutationResult<
    CountyCourtOutreachReplySendResponse,
    unknown,
    { replyId: string; payload: CountyCourtOutreachReplySendRequest }
  >;
  neverEmailMutation: UseMutationResult<
    CountyCourtOutreachNeverEmailResponse,
    unknown,
    number
  >;
}) {
  const [composing, setComposing] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [includeQuoted, setIncludeQuoted] = useState(true);
  const [attachFiles, setAttachFiles] = useState<File[]>([]);

  const alreadyReplied = Boolean(reply?.manual_reply_sent_at);
  const sendLocked =
    alreadyReplied ||
    sendReplyMutation.isSuccess ||
    sendReplyMutation.isPending;
  const neverEmailed =
    reply?.matched_contact?.status === 'removed' ||
    neverEmailMutation.isSuccess;

  useEffect(() => {
    if (!reply) return;
    setReplySubject(dedupeReplySubject(reply.subject));
    setReplyBody('');
    setIncludeQuoted(true);
    setAttachFiles([]);
    setComposing(false);
    neverEmailMutation.reset();
  }, [reply?.id]);

  if (!open) return null;

  const canReply = Boolean(reply?.from_email?.trim());
  const noThreading = reply != null && !reply.message_id;

  const handleSend = () => {
    if (!replyId || !reply || sendLocked) return;
    const body = replyBody.trim();
    if (!body) return;
    if (!reply.matched_contact) {
      const ok = window.confirm(
        "Couldn't match this address to a county court contact. " +
          'The reply will still go out but will not be logged against a contact. Continue?'
      );
      if (!ok) return;
    }
    if (noThreading) {
      const ok = window.confirm(
        'This inbound email has no Message-ID, so your reply will start a new thread instead of continuing the existing conversation. Continue?'
      );
      if (!ok) return;
    }
    sendReplyMutation.mutate({
      replyId,
      payload: {
        subject: replySubject.trim() || dedupeReplySubject(reply.subject),
        body,
        include_quoted_original: includeQuoted,
        files: attachFiles,
      },
    });
  };

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'
      onClick={onClose}
    >
      <div
        className='bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col'
        onClick={(e) => e.stopPropagation()}
      >
        <header className='flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4'>
          <div className='min-w-0'>
            <h2 className='text-lg font-semibold text-gray-900'>
              Reply
              {reply?.matched_contact ? (
                <span className='ml-2 text-sm font-normal text-gray-600'>
                  from {matchedCountyLabel(reply.matched_contact)}
                </span>
              ) : null}
            </h2>
            {reply?.received_at ? (
              <p className='mt-0.5 text-xs text-gray-500'>
                Received {formatRelative(reply.received_at)} ·{' '}
                {formatTimestamp(reply.received_at)}
              </p>
            ) : null}
            {alreadyReplied && reply?.manual_reply_sent_at ? (
              <p className='mt-1 text-xs font-medium text-blue-800'>
                You replied {formatRelative(reply.manual_reply_sent_at)} ·{' '}
                {formatTimestamp(reply.manual_reply_sent_at)}
              </p>
            ) : null}
          </div>
          <div className='flex shrink-0 items-center gap-2'>
            {reply?.matched_contact ? (
              <button
                type='button'
                disabled={neverEmailed || neverEmailMutation.isPending}
                onClick={() => {
                  const contact = reply.matched_contact;
                  if (!contact) return;
                  const ok = window.confirm(
                    `Never email ${matchedCountyLabel(contact)} again? ` +
                      'Approved drafts will be skipped and they will leave the gym.',
                  );
                  if (!ok) return;
                  neverEmailMutation.mutate(contact.id);
                }}
                className='rounded-md border border-red-200 bg-red-50 px-3 py-1 text-sm font-semibold text-red-800 hover:bg-red-100 disabled:opacity-50'
              >
                {neverEmailed
                  ? 'Never email (on)'
                  : neverEmailMutation.isPending
                    ? 'Saving…'
                    : 'Never email again'}
              </button>
            ) : null}
            {reply && canReply && !alreadyReplied ? (
              <button
                type='button'
                onClick={() => setComposing((v) => !v)}
                disabled={sendLocked}
                title={
                  noThreading
                    ? 'No Message-ID on this inbound — reply will start a new thread'
                    : undefined
                }
                className='rounded-md border border-primary bg-primary px-3 py-1 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50'
              >
                {composing
                  ? 'Hide composer'
                  : noThreading
                    ? 'Reply (new thread)'
                    : 'Reply'}
              </button>
            ) : null}
            <button
              type='button'
              onClick={onClose}
              className='rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100'
            >
              Close
            </button>
          </div>
        </header>

        {loading ? (
          <div className='p-6 text-sm text-gray-500'>Loading reply...</div>
        ) : error ? (
          <div className='m-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
            {error}
          </div>
        ) : reply ? (
          <>
            {alreadyReplied && reply.manual_reply_sent_at ? (
              <div className='border-b border-blue-200 bg-blue-50 px-5 py-3 text-sm text-blue-900'>
                You already sent a manual reply to this message{' '}
                {formatRelative(reply.manual_reply_sent_at)} (
                {formatTimestamp(reply.manual_reply_sent_at)}).
              </div>
            ) : null}
            {neverEmailed ? (
              <div className='border-b border-red-200 bg-red-50 px-5 py-3 text-sm text-red-900'>
                This contact is marked never email again. They will not show in
                the gym and the sender will skip them.
              </div>
            ) : null}
            {neverEmailMutation.isError ? (
              <div className='border-b border-red-200 bg-red-50 px-5 py-3 text-sm text-red-900'>
                {parseDetail(neverEmailMutation.error)}
              </div>
            ) : null}
            <div className='border-b border-gray-200 bg-gray-50 px-5 py-3 text-xs text-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1'>
              <div className='flex gap-2'>
                <span className='font-semibold text-gray-500 w-16'>From</span>
                <span className='truncate'>{reply.from_email}</span>
              </div>
              <div className='flex gap-2'>
                <span className='font-semibold text-gray-500 w-16'>To</span>
                <span className='truncate'>
                  {reply.to_emails.join(', ') || '—'}
                </span>
              </div>
              <div className='flex gap-2 sm:col-span-2'>
                <span className='font-semibold text-gray-500 w-16'>
                  Subject
                </span>
                <span className='truncate'>
                  {reply.subject || '(no subject)'}
                </span>
              </div>
              {reply.matched_contact ? (
                <div className='flex gap-2 sm:col-span-2'>
                  <span className='font-semibold text-gray-500 w-16'>
                    Matched
                  </span>
                  <span className='truncate'>
                    {matchedCountyLabel(reply.matched_contact)} ·{' '}
                    <code className='text-[11px] bg-gray-100 px-1 rounded'>
                      contact #{reply.matched_contact.id} ·{' '}
                      status={reply.matched_contact.status}
                    </code>
                  </span>
                </div>
              ) : null}
              {reply.attachments.length > 0 ? (
                <div className='flex gap-2 sm:col-span-2'>
                  <span className='font-semibold text-gray-500 w-16'>
                    Attach.
                  </span>
                  <span className='truncate'>
                    {reply.attachments
                      .map(
                        (a) =>
                          a.filename ||
                          a.content_type ||
                          a.id ||
                          '(unnamed)'
                      )
                      .join(', ')}
                  </span>
                </div>
              ) : null}
            </div>

            {composing ? (
              <div className='border-b border-gray-200 bg-white px-5 py-4 space-y-3'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Compose reply
                </p>
                <div className='grid grid-cols-1 gap-2 text-sm'>
                  <label className='flex gap-2 items-baseline'>
                    <span className='font-semibold text-gray-500 w-14 shrink-0'>
                      From
                    </span>
                    <span className='text-gray-700 truncate'>
                      {COUNTY_COURT_OUTREACH_FROM_DISPLAY}
                    </span>
                  </label>
                  <label className='flex gap-2 items-baseline'>
                    <span className='font-semibold text-gray-500 w-14 shrink-0'>
                      To
                    </span>
                    <span className='text-gray-700 truncate'>
                      {reply.from_email}
                    </span>
                  </label>
                  <label className='flex flex-col gap-1'>
                    <span className='font-semibold text-gray-500'>Subject</span>
                    <input
                      type='text'
                      value={replySubject}
                      onChange={(e) => setReplySubject(e.target.value)}
                      disabled={sendLocked}
                      className='w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-50'
                    />
                  </label>
                  <label className='flex flex-col gap-1'>
                    <span className='font-semibold text-gray-500'>Message</span>
                    <textarea
                      rows={8}
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      disabled={sendLocked}
                      autoFocus
                      placeholder='Write your reply…'
                      className='w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm font-mono disabled:bg-gray-50'
                    />
                  </label>
                  <label className='flex items-center gap-2 text-sm text-gray-700'>
                    <input
                      type='checkbox'
                      checked={includeQuoted}
                      onChange={(e) => setIncludeQuoted(e.target.checked)}
                      disabled={sendLocked}
                    />
                    Include quoted original below
                  </label>
                  <OutreachReplyAttachmentPicker
                    files={attachFiles}
                    onChange={setAttachFiles}
                    disabled={sendLocked}
                  />
                </div>
                {sendReplyMutation.isError ? (
                  <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                    {parseDetail(sendReplyMutation.error)}
                  </div>
                ) : null}
                {sendReplyMutation.isSuccess && sendReplyMutation.data ? (
                  <div className='rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900'>
                    Sent {formatTimestamp(sendReplyMutation.data.sent_at)} — Resend
                    id{' '}
                    <code className='text-xs'>
                      {sendReplyMutation.data.message_id}
                    </code>
                    .
                    {sendReplyMutation.data.contact_status_after
                      ? ' Contact marked replied.'
                      : ' Not logged to a contact.'}
                    {sendReplyMutation.data.attachment_filenames?.length
                      ? ` Attached ${sendReplyMutation.data.attachment_filenames.join(', ')}.`
                      : ''}
                  </div>
                ) : null}
                <div className='flex justify-end'>
                  <button
                    type='button'
                    onClick={handleSend}
                    disabled={sendLocked || replyBody.trim().length === 0}
                    className='rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50'
                  >
                    {sendReplyMutation.isPending ? 'Sending…' : 'Send reply'}
                  </button>
                </div>
              </div>
            ) : null}

            <div className='border-b border-gray-200 px-5 pt-3'>
              <div className='inline-flex rounded-md border border-gray-300 overflow-hidden text-sm'>
                <button
                  type='button'
                  onClick={() => onModeChange('html')}
                  disabled={!reply.html}
                  className={`px-3 py-1.5 ${
                    mode === 'html'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40'
                  }`}
                >
                  HTML
                </button>
                <button
                  type='button'
                  onClick={() => onModeChange('text')}
                  disabled={!reply.text}
                  className={`px-3 py-1.5 border-l border-gray-300 ${
                    mode === 'text'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40'
                  }`}
                >
                  Plain text
                </button>
              </div>
            </div>

            <div className='flex-1 min-h-0 overflow-auto bg-gray-100 p-4'>
              {mode === 'html' && reply.html ? (
                <iframe
                  title='Reply HTML'
                  srcDoc={reply.html}
                  sandbox=''
                  className='w-full h-[min(40vh,360px)] bg-white rounded shadow border border-gray-200'
                />
              ) : reply.text ? (
                <pre className='w-full bg-white rounded shadow border border-gray-200 p-4 text-sm font-mono whitespace-pre-wrap text-gray-800 max-h-[min(40vh,360px)] overflow-auto'>
                  {reply.text}
                </pre>
              ) : (
                <p className='text-sm text-gray-500 italic'>
                  No body returned by Resend for this reply.
                </p>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function PreviewDialog({
  result,
  mode,
  onModeChange,
  onClose,
}: {
  result: PreviewResponse | null;
  mode: 'html' | 'text';
  onModeChange: (m: 'html' | 'text') => void;
  onClose: () => void;
}) {
  if (!result) return null;
  const listUnsub = result.headers['List-Unsubscribe'] ?? '';
  const listUnsubPost = result.headers['List-Unsubscribe-Post'] ?? '';
  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'
      onClick={onClose}
    >
      <div
        className='bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col'
        onClick={(e) => e.stopPropagation()}
      >
        <header className='flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4'>
          <div className='min-w-0'>
            <h2 className='text-lg font-semibold text-gray-900'>
              Preview &mdash; what the recipient will see
            </h2>
            <p className='mt-0.5 text-xs text-gray-500'>
              Plain-text send, same as Celery. HTML is the same words with
              clickable links, no newsletter card. Leftover{' '}
              <code className='bg-gray-100 px-1 rounded'>{'{{placeholders}}'}</code>{' '}
              go out as written.
            </p>
          </div>
          <button
            type='button'
            onClick={onClose}
            className='shrink-0 rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100'
            aria-label='Close preview'
          >
            Close
          </button>
        </header>

        <div className='border-b border-gray-200 bg-gray-50 px-5 py-3 text-xs text-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1'>
          <div className='flex gap-2'>
            <span className='font-semibold text-gray-500 w-16'>From</span>
            <span className='truncate'>{result.from_email}</span>
          </div>
          <div className='flex gap-2'>
            <span className='font-semibold text-gray-500 w-16'>To</span>
            <span className='truncate'>
              {result.to_email || (
                <em className='text-amber-700'>
                  no email on file (would skip at send time)
                </em>
              )}
            </span>
          </div>
          <div className='flex gap-2 sm:col-span-2'>
            <span className='font-semibold text-gray-500 w-16'>Subject</span>
            <span className='truncate'>{result.subject}</span>
          </div>
          {result.reply_to ? (
            <div className='flex gap-2'>
              <span className='font-semibold text-gray-500 w-16'>
                Reply-To
              </span>
              <span className='truncate'>{result.reply_to}</span>
            </div>
          ) : null}
          <div className='flex gap-2 sm:col-span-2'>
            <span className='font-semibold text-gray-500 w-16'>
              Jurisdiction
            </span>
            <span className='truncate'>{jurisdictionLabel(result)}</span>
          </div>
          <div className='flex gap-2 sm:col-span-2'>
            <span className='font-semibold text-gray-500 w-16'>
              Unsubscribe
            </span>
            <a
              href={result.unsubscribe_url}
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary hover:underline truncate'
              title={result.unsubscribe_url}
            >
              {result.unsubscribe_url}
            </a>
          </div>
          {listUnsub ? (
            <div className='flex gap-2 sm:col-span-2'>
              <span className='font-semibold text-gray-500 w-16'>Headers</span>
              <span className='truncate text-gray-600'>
                <code className='text-[11px]'>List-Unsubscribe: {listUnsub}</code>
                {listUnsubPost ? (
                  <>
                    <br />
                    <code className='text-[11px]'>
                      List-Unsubscribe-Post: {listUnsubPost}
                    </code>
                  </>
                ) : null}
              </span>
            </div>
          ) : null}
        </div>

        <div className='border-b border-gray-200 px-5 pt-3'>
          <div className='inline-flex rounded-md border border-gray-300 overflow-hidden text-sm'>
            <button
              type='button'
              onClick={() => onModeChange('html')}
              className={`px-3 py-1.5 ${
                mode === 'html'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              HTML
            </button>
            <button
              type='button'
              onClick={() => onModeChange('text')}
              className={`px-3 py-1.5 border-l border-gray-300 ${
                mode === 'text'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Plain text
            </button>
          </div>
        </div>

        <div className='flex-1 min-h-0 overflow-auto bg-gray-100 p-4'>
          {mode === 'html' ? (
            <iframe
              title='Email HTML preview'
              srcDoc={previewHtmlSrcDoc(result.html)}
              sandbox='allow-popups allow-popups-to-escape-sandbox'
              className='w-full h-[60vh] bg-white rounded shadow border border-gray-200'
            />
          ) : (
            <PlainTextPreview text={result.text} />
          )}
        </div>
      </div>
    </div>
  );
}
